import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
  Avatar,
  Grid,
  Checkbox,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Note as NoteIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  MarkEmailRead as MarkEmailReadIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { apiRequest } from "../config/api";
import { SeasonRegistration, Player, Season, GearDesign } from "../types";
import Layout from "../components/Layout";
import GearDesignPicker from "../components/GearDesignPicker";
import ImageUploadService from "../services/imageUpload";
import * as XLSX from "xlsx";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`season-reg-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface BankDetailsFormProps {
  initialIban: string;
  initialBic: string;
  initialAccountName: string;
  initialBankName: string;
  onError: (msg: string) => void;
}

function BankDetailsForm({
  initialIban,
  initialBic,
  initialAccountName,
  initialBankName,
  onError,
}: BankDetailsFormProps) {
  const [iban, setIban] = useState(initialIban);
  const [bic, setBic] = useState(initialBic);
  const [accountName, setAccountName] = useState(initialAccountName);
  const [bankName, setBankName] = useState(initialBankName);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setIban(initialIban);
  }, [initialIban]);
  useEffect(() => {
    setBic(initialBic);
  }, [initialBic]);
  useEffect(() => {
    setAccountName(initialAccountName);
  }, [initialAccountName]);
  useEffect(() => {
    setBankName(initialBankName);
  }, [initialBankName]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiRequest(
        "/api/season-registration/bank-details",
        {
          method: "PUT",
          body: JSON.stringify({ iban, bic, accountName, bankName }),
        },
      );
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.error || "Failed to save bank details");
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      onError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
        🏦 Bank Details (for player payments)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These details will be shown on each player's confirmation page and
        included in invitation emails.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Account Name"
          value={accountName}
          onChange={(e) => setAccountName(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          label="Bank Name (optional)"
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          label="IBAN"
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          sx={{ flex: 1, minWidth: 250 }}
          inputProps={{ style: { fontFamily: "monospace" } }}
        />
        <TextField
          label="BIC / SWIFT"
          value={bic}
          onChange={(e) => setBic(e.target.value)}
          sx={{ flex: 1, minWidth: 150 }}
          inputProps={{ style: { fontFamily: "monospace" } }}
        />
      </Box>
      {saved && (
        <Alert severity="success" sx={{ mb: 1 }}>
          Bank details saved!
        </Alert>
      )}
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving || !iban || !bic || !accountName}
      >
        {saving ? <CircularProgress size={18} /> : "Save Bank Details"}
      </Button>
    </Box>
  );
}

const SeasonRegistrations: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [registrations, setRegistrations] = useState<SeasonRegistration[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<
    string | null
  >(null);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [seasonName, setSeasonName] = useState("Summer 2026");
  const [registrationOpenDate, setRegistrationOpenDate] = useState<string>("");
  const [estimatedPrice, setEstimatedPrice] = useState<string>("");
  const [tshirtPrice, setTshirtPrice] = useState<string>("");
  const [shortsPrice, setShortsPrice] = useState<string>("");
  const [kitPrice, setKitPrice] = useState<string>("");
  const [trialDayDate, setTrialDayDate] = useState<string>("");
  const [inviteIncludeInvited, setInviteIncludeInvited] = useState(false);
  // One-off note added to the top of the emails of the batch being sent (template stays untouched)
  const [inviteNote, setInviteNote] = useState("");
  const [invitePreview, setInvitePreview] = useState<{
    open: boolean;
    loading: boolean;
    html: string;
    subject: string;
    playerName: string;
  }>({ open: false, loading: false, html: "", subject: "", playerName: "" });
  const [trialInviteDialogOpen, setTrialInviteDialogOpen] = useState(false);
  const [trialInviteIncludeInvited, setTrialInviteIncludeInvited] =
    useState(false);
  const [sendingTrialInvites, setSendingTrialInvites] = useState(false);
  const [trialInviteResult, setTrialInviteResult] = useState<{
    sent: number;
    failed: string[];
  } | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [midSeasonIsOpen, setMidSeasonIsOpen] = useState(false);
  const [midSeasonPrice, setMidSeasonPrice] = useState<string>("");
  const [midSeasonGearEnabled, setMidSeasonGearEnabled] = useState(false);
  const [gearEditingEnabled, setGearEditingEnabled] = useState(false);
  const [tshirtSizeChartUrl, setTshirtSizeChartUrl] = useState<string>("");
  const [poloSizeChartUrl, setPoloSizeChartUrl] = useState<string>("");
  const [shortsSizeChartUrl, setShortsSizeChartUrl] = useState<string>("");
  const [sizeChartUploading, setSizeChartUploading] = useState<
    "tshirt" | "polo" | "shorts" | null
  >(null);

  // Season scoping: which season registrations get filed under (persisted setting)
  // vs. which season the admin is currently browsing (view-only, not persisted).
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [registrationSeasonId, setRegistrationSeasonId] = useState<string>("");
  const [viewSeasonId, setViewSeasonId] = useState<string>("");

  // Cancellation policy state
  const [policyText, setPolicyText] = useState([
    "Your registration payment covers 5 free league day participations",
    "Full refund available: Cancel before May 1st (season start)",
    "No refund after May 1st: Once the season starts, refunds are not possible regardless of attendance",
  ]);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const [policySuccess, setPolicySuccess] = useState<string | null>(null);

  // Gear design catalog state
  const [gearDesigns, setGearDesigns] = useState<GearDesign[]>([]);
  const [gearDesignsLoading, setGearDesignsLoading] = useState(false);
  const [designDialogOpen, setDesignDialogOpen] = useState(false);
  const [editingDesign, setEditingDesign] = useState<GearDesign | null>(null);
  const [designName, setDesignName] = useState("");
  const [designImageUrl, setDesignImageUrl] = useState("");
  const [designActive, setDesignActive] = useState(true);
  const [designCategory, setDesignCategory] = useState<
    "tshirt" | "polo" | "shorts"
  >("tshirt");
  const [designSaving, setDesignSaving] = useState(false);
  const [designImageUploading, setDesignImageUploading] = useState(false);
  const [deleteDesignDialogOpen, setDeleteDesignDialogOpen] = useState(false);
  const [designToDelete, setDesignToDelete] = useState<GearDesign | null>(
    null,
  );

  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailStatusFilter, setEmailStatusFilter] = useState<string>("all");
  const [emailPlayerTypeFilter, setEmailPlayerTypeFilter] =
    useState<string>("all");
  const [emailTrialDayFilter, setEmailTrialDayFilter] = useState<string>("all");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  // Clarification dialog state
  const [clarificationDialogOpen, setClarificationDialogOpen] = useState(false);
  const [clarificationNote, setClarificationNote] = useState("");
  const [selectedRegistrationForNote, setSelectedRegistrationForNote] =
    useState<string | null>(null);

  // Player note dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [playerNote, setPlayerNote] = useState("");
  const [selectedPlayerForNote, setSelectedPlayerForNote] = useState<
    string | null
  >(null);
  const [savingNote, setSavingNote] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [preferredLevelFilter, setPreferredLevelFilter] =
    useState<string>("all");
  const [trialDayFilter, setTrialDayFilter] = useState<string>("all");
  const [phaseFilter, setPhaseFilter] = useState<string>("all");

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStatusFilter, setExportStatusFilter] = useState<string>("all");

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] =
    useState<SeasonRegistration | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Registration answers detail dialog state
  const [answersDialogOpen, setAnswersDialogOpen] = useState(false);
  const [selectedRegistrationForAnswers, setSelectedRegistrationForAnswers] =
    useState<SeasonRegistration | null>(null);

  // Division detail dialog state
  const [divisionDetailDialogOpen, setDivisionDetailDialogOpen] =
    useState(false);
  const [selectedDivisionForDetail, setSelectedDivisionForDetail] = useState<
    string | null
  >(null);

  // Planning division detail dialog state
  const [planningDetailDialogOpen, setPlanningDetailDialogOpen] =
    useState(false);
  const [selectedPlanningDivision, setSelectedPlanningDivision] = useState<
    string | null
  >(null);

  // Division assignment state (for planning)
  const [assignedDivisions, setAssignedDivisions] = useState<{
    [registrationId: string]: string;
  }>({});

  // Resend email state
  const [resendingEmailId, setResendingEmailId] = useState<string | null>(null);

  // Confirmation link state
  const [confirmationLinksActive, setConfirmationLinksActive] = useState(false);
  const [togglingLinks, setTogglingLinks] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [showHeaderButton, setShowHeaderButton] = useState(true);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [copiedReminderItem, setCopiedReminderItem] = useState<{
    id: string;
    type: "phone" | "message";
  } | null>(null);
  const [resendingInvitationId, setResendingInvitationId] = useState<
    string | null
  >(null);

  // Bank details state (initial values only — editing state lives in <BankDetailsForm>)
  const [bankIban, setBankIban] = useState("");
  const [bankBic, setBankBic] = useState("");
  const [bankAccountName, setBankAccountName] = useState("");
  const [bankBankName, setBankBankName] = useState("");

  // Send invitations modal state
  const [sendInvitationsDialogOpen, setSendInvitationsDialogOpen] =
    useState(false);
  const [sendingInvitations, setSendingInvitations] = useState(false);
  const [invitationResult, setInvitationResult] = useState<{
    sent: number;
    failed: string[];
  } | null>(null);
  const [generatingTokens, setGeneratingTokens] = useState(false);
  const [savingDivisions, setSavingDivisions] = useState(false);

  // Send missing invitations (projected/default only) modal state
  const [sendMissingInvitationsDialogOpen, setSendMissingInvitationsDialogOpen] =
    useState(false);
  const [sendingMissingInvitations, setSendingMissingInvitations] = useState(false);
  const [missingInvitationResult, setMissingInvitationResult] = useState<{
    sent: number;
    failed: string[];
  } | null>(null);

  // Apology emails for invitations sent by mistake to registrations of other seasons
  interface MistakenRecipient {
    id: string;
    playerName: string;
    email: string;
    seasonId: string | null;
    invitationSentAt: string;
    alsoRegisteredThisSeason: boolean;
  }
  const [apologyDialogOpen, setApologyDialogOpen] = useState(false);
  const [apologyLoading, setApologyLoading] = useState(false);
  type ApologyGroup = "apology" | "duplicate";
  const [apologySending, setApologySending] = useState<ApologyGroup | null>(
    null,
  );
  const [duplicateSubject, setDuplicateSubject] = useState("");
  const [duplicateBody, setDuplicateBody] = useState("");
  const [duplicateResult, setDuplicateResult] = useState<{
    sent: number;
    failed: string[];
  } | null>(null);
  const [apologyRecipients, setApologyRecipients] = useState<
    MistakenRecipient[]
  >([]);
  const [apologySince, setApologySince] = useState(() => {
    const d = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  });
  const [apologySubject, setApologySubject] = useState("");
  const [apologyBody, setApologyBody] = useState("");
  const [apologyResult, setApologyResult] = useState<{
    sent: number;
    failed: string[];
  } | null>(null);

  const loadApologyRecipients = async (since: string) => {
    try {
      setApologyLoading(true);
      setApologyResult(null);
      setDuplicateResult(null);
      const params = new URLSearchParams({
        since: new Date(since).toISOString(),
      });
      if (viewSeasonId) params.set("seasonId", viewSeasonId);
      const response = await apiRequest(
        `/api/season-registration/mistaken-invitations?${params.toString()}`,
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to load list");
      setApologyRecipients(data.recipients);
      setApologySubject((prev) => prev || data.apology.subject);
      setApologyBody((prev) => prev || data.apology.htmlBody);
      setDuplicateSubject((prev) => prev || data.duplicate.subject);
      setDuplicateBody((prev) => prev || data.duplicate.htmlBody);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setApologyLoading(false);
    }
  };

  const groupRecipients = (group: ApologyGroup) =>
    apologyRecipients.filter(
      (r) => r.alsoRegisteredThisSeason === (group === "duplicate"),
    );
  const uniqueEmailCount = (list: MistakenRecipient[]) =>
    new Set(list.map((r) => r.email.trim().toLowerCase())).size;

  const handleSendApology = async (group: ApologyGroup) => {
    const setResult =
      group === "apology" ? setApologyResult : setDuplicateResult;
    try {
      setApologySending(group);
      setResult(null);
      const response = await apiRequest(
        "/api/season-registration/mistaken-invitations/apology",
        {
          method: "POST",
          body: JSON.stringify({
            group,
            seasonId: viewSeasonId,
            registrationIds: groupRecipients(group).map((r) => r.id),
            subject: group === "apology" ? apologySubject : duplicateSubject,
            htmlBody: group === "apology" ? apologyBody : duplicateBody,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to send");
      setResult({ sent: data.sent, failed: data.failed || [] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setApologySending(null);
    }
  };

  // Welcome email to the approved new players of the viewed season
  interface WelcomeRecipient {
    id: string;
    playerName: string;
    email: string;
    username: string;
    division: string;
    welcomeEmailSentAt: string | null;
  }
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeRecipients, setWelcomeRecipients] = useState<WelcomeRecipient[]>([]);
  const [welcomeNotApproved, setWelcomeNotApproved] = useState<
    { id: string; playerName: string; email: string; username: string; status: string }[]
  >([]);
  const [welcomeSelected, setWelcomeSelected] = useState<Set<string>>(new Set());
  const [welcomeSubject, setWelcomeSubject] = useState("");
  const [welcomeBody, setWelcomeBody] = useState("");
  const [welcomeResult, setWelcomeResult] = useState<{
    sent: number;
    failed: string[];
  } | null>(null);

  const openWelcomeDialog = async () => {
    setWelcomeDialogOpen(true);
    setWelcomeResult(null);
    try {
      setWelcomeLoading(true);
      const params = new URLSearchParams();
      if (viewSeasonId) params.set("seasonId", viewSeasonId);
      const response = await apiRequest(
        `/api/season-registration/welcome-email/recipients?${params.toString()}`,
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to load recipients");
      setWelcomeRecipients(data.recipients);
      setWelcomeNotApproved(data.notApproved || []);
      // Players who already got the welcome email start unselected
      setWelcomeSelected(
        new Set(
          (data.recipients as WelcomeRecipient[])
            .filter((r) => !r.welcomeEmailSentAt)
            .map((r) => r.id),
        ),
      );
      setWelcomeSubject(data.subject);
      setWelcomeBody(data.htmlBody);
    } catch (err: any) {
      setError(err.message);
      setWelcomeDialogOpen(false);
    } finally {
      setWelcomeLoading(false);
    }
  };

  const handleSendWelcomeEmails = async () => {
    try {
      setWelcomeSending(true);
      setWelcomeResult(null);
      const response = await apiRequest("/api/season-registration/welcome-email/send", {
        method: "POST",
        body: JSON.stringify({
          seasonId: viewSeasonId,
          registrationIds: Array.from(welcomeSelected),
          subject: welcomeSubject,
          htmlBody: welcomeBody,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to send");
      setWelcomeResult({ sent: data.sent, failed: data.failed || [] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWelcomeSending(false);
    }
  };

  // Preview uses the first selected recipient (or the first listed) as the sample
  const welcomeSample =
    welcomeRecipients.find((r) => welcomeSelected.has(r.id)) || welcomeRecipients[0];
  const fillWelcome = (text: string) =>
    welcomeSample
      ? text
          .replace(/\{\{playerName\}\}/g, welcomeSample.playerName)
          .replace(/\{\{username\}\}/g, welcomeSample.username)
          .replace(/\{\{division\}\}/g, welcomeSample.division)
      : text;

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyText = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
    } catch {
      setError("Could not copy to the clipboard. Select the text and copy it manually.");
    }
  };

  const groupEmailList = (group: ApologyGroup) =>
    Array.from(
      new Set(groupRecipients(group).map((r) => r.email.trim().toLowerCase())),
    ).join(", ");

  // Plain-text version of the HTML message, for pasting into Gmail.
  // {{playerName}} can't be personalised in a manual bulk mail, so the greeting becomes "Hi,".
  const htmlToPlainText = (html: string) =>
    html
      .replace(/Hi\s*\{\{playerName\}\}/gi, "Hi")
      .replace(/\{\{playerName\}\}/g, "")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  useEffect(() => {
    const init = async () => {
      fetchPlayers();
      fetchCancellationPolicy();
      fetchBankDetails();
      fetchGearDesigns();

      const [seasonsList, statusSeasonId] = await Promise.all([
        fetchSeasons(),
        fetchRegistrationStatus(),
      ]);

      // Default the "viewing" season to whichever season registration is
      // currently configured to be open for; fall back to the active season
      // (or the most recent one) if that isn't set yet — e.g. before the
      // seasonId backfill migration has run.
      const defaultSeasonId =
        statusSeasonId ||
        seasonsList.find((s) => s.isActive)?.id ||
        seasonsList[0]?.id ||
        "";

      setViewSeasonId(defaultSeasonId);
      fetchRegistrations(defaultSeasonId);
    };
    init();
  }, []);

  // Filter registrations based on status
  const filteredRegistrations = registrations.filter((reg) => {
    // Status filter
    if (statusFilter !== "all" && reg.status !== statusFilter) {
      return false;
    }
    // Preferred level filter
    if (preferredLevelFilter !== "all") {
      if (!reg.preferredDivisionLevel) return false;
      if (reg.preferredDivisionLevel !== preferredLevelFilter) return false;
    }
    // Trial day filter
    if (trialDayFilter !== "all") {
      const wantsTrialDay = reg.wantsTrialDay || false;
      if (trialDayFilter === "yes" && !wantsTrialDay) return false;
      if (trialDayFilter === "no" && wantsTrialDay) return false;
    }
    // Phase filter
    if (phaseFilter !== "all") {
      const phase = reg.registrationPhase || "initial";
      if (phase !== phaseFilter) return false;
    }
    return true;
  });

  const fetchRegistrationStatus = async (): Promise<string> => {
    try {
      const response = await apiRequest("/api/season-registration/status");
      if (response.ok) {
        const data = await response.json();
        setIsRegistrationOpen(data.isOpen);
        setSeasonName(data.seasonName || "Summer 2026");
        setRegistrationSeasonId(data.seasonId || "");
        setEstimatedPrice(
          data.estimatedPrice ? String(data.estimatedPrice) : "",
        );
        setTshirtPrice(data.tshirtPrice ? String(data.tshirtPrice) : "");
        setShortsPrice(data.shortsPrice ? String(data.shortsPrice) : "");
        setKitPrice(data.kitPrice ? String(data.kitPrice) : "");
        setTrialDayDate(data.trialDayDate || "");
        setTshirtSizeChartUrl(data.tshirtSizeChartUrl || "");
        setPoloSizeChartUrl(data.poloSizeChartUrl || "");
        setShortsSizeChartUrl(data.shortsSizeChartUrl || "");
        setConfirmationLinksActive(data.confirmationLinksActive ?? false);
        setShowPopup(data.showPopup ?? true);
        setShowHeaderButton(data.showHeaderButton ?? true);
        setMidSeasonIsOpen(data.midSeasonIsOpen ?? false);
        setMidSeasonPrice(
          data.midSeasonPrice ? String(data.midSeasonPrice) : "",
        );
        setMidSeasonGearEnabled(data.midSeasonGearEnabled ?? false);
        setGearEditingEnabled(data.gearEditingEnabled ?? false);
        if (data.registrationOpenDate) {
          const date = new Date(data.registrationOpenDate);
          // Format to datetime-local input format (YYYY-MM-DDTHH:mm)
          const formattedDate = date.toISOString().slice(0, 16);
          setRegistrationOpenDate(formattedDate);
        } else {
          setRegistrationOpenDate("");
        }
        return data.seasonId || "";
      }
    } catch (err) {
      console.error("Failed to fetch registration status:", err);
    }
    return "";
  };

  const fetchSeasons = async (): Promise<Season[]> => {
    try {
      const response = await apiRequest("/api/seasons");
      if (response.ok) {
        const data = await response.json();
        setSeasons(data);
        return data;
      }
    } catch (err) {
      console.error("Failed to fetch seasons:", err);
    }
    return [];
  };

  const fetchGearDesigns = async () => {
    setGearDesignsLoading(true);
    try {
      const response = await apiRequest(
        "/api/season-registration/gear-designs/all",
      );
      if (response.ok) {
        const data = await response.json();
        setGearDesigns(data);
      }
    } catch (err) {
      console.error("Failed to fetch gear designs:", err);
    } finally {
      setGearDesignsLoading(false);
    }
  };

  const buildSettingsPayload = (overrides: Record<string, any> = {}) => ({
    isOpen: isRegistrationOpen,
    seasonName,
    seasonId: registrationSeasonId || null,
    registrationOpenDate: registrationOpenDate || null,
    estimatedPrice: estimatedPrice || null,
    tshirtPrice: tshirtPrice || null,
    shortsPrice: shortsPrice || null,
    kitPrice: kitPrice || null,
    trialDayDate: trialDayDate || null,
    confirmationLinksActive,
    showPopup,
    showHeaderButton,
    midSeasonIsOpen,
    midSeasonPrice: midSeasonPrice || null,
    midSeasonGearEnabled,
    gearEditingEnabled,
    tshirtSizeChartUrl: tshirtSizeChartUrl || null,
    poloSizeChartUrl: poloSizeChartUrl || null,
    shortsSizeChartUrl: shortsSizeChartUrl || null,
    ...overrides,
  });

  // Change which season new registrations are filed under (persisted setting).
  // Auto-fills the display season name from the picked season, kept editable
  // separately for display-text tweaks in emails.
  const handleRegistrationSeasonChange = async (newSeasonId: string) => {
    const picked = seasons.find((s) => s.id === newSeasonId);
    const newSeasonName = picked?.name || seasonName;
    setRegistrationSeasonId(newSeasonId);
    setSeasonName(newSeasonName);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ seasonId: newSeasonId, seasonName: newSeasonName }),
        ),
      });
    } catch (err) {
      console.error("Failed to update registration season:", err);
    }
  };

  // Change which season's registrations the admin is browsing — view-only,
  // not persisted. Past seasons render read-only (see isViewingOpenSeason).
  const handleViewSeasonChange = (newSeasonId: string) => {
    setViewSeasonId(newSeasonId);
    fetchRegistrations(newSeasonId);
  };

  const handleToggleRegistration = async (checked: boolean) => {
    try {
      setStatusLoading(true);
      const response = await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(buildSettingsPayload({ isOpen: checked })),
      });

      if (!response.ok) {
        throw new Error("Failed to update registration status");
      }

      setIsRegistrationOpen(checked);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to update registration status");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSeasonNameChange = async (newName: string) => {
    setSeasonName(newName);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(buildSettingsPayload({ seasonName: newName })),
      });
    } catch (err) {
      console.error("Failed to update season name:", err);
    }
  };

  const handleRegistrationDateChange = async (newDate: string) => {
    setRegistrationOpenDate(newDate);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ registrationOpenDate: newDate || null }),
        ),
      });
    } catch (err) {
      console.error("Failed to update registration open date:", err);
    }
  };

  const handleEstimatedPriceChange = async (newPrice: string) => {
    setEstimatedPrice(newPrice);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ estimatedPrice: newPrice || null }),
        ),
      });
    } catch (err) {
      console.error("Failed to update estimated price:", err);
    }
  };

  const handleGearPricesChange = async (field: string, value: string) => {
    if (field === "tshirtPrice") setTshirtPrice(value);
    if (field === "shortsPrice") setShortsPrice(value);
    if (field === "kitPrice") setKitPrice(value);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(buildSettingsPayload({ [field]: value || null })),
      });
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
    }
  };

  const saveTrialDayDate = async (value: string) => {
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(buildSettingsPayload({ trialDayDate: value || null })),
      });
    } catch (err) {
      console.error("Failed to update trial day date:", err);
    }
  };

  const trialDayTargets = () =>
    registrations.filter(
      (r) =>
        assignedDivisions[r.id!] === "Trial Day" &&
        r.status !== "rejected" &&
        (trialInviteIncludeInvited || !(r as any).invitationSentAt),
    );

  const mainInviteTargets = () =>
    registrations.filter(
      (r) =>
        assignedDivisions[r.id!] &&
        r.status !== "rejected" &&
        (inviteIncludeInvited || !(r as any).invitationSentAt),
    );

  const missingInviteTargets = () =>
    registrations.filter(
      (r) =>
        assignedDivisions[r.id!] &&
        !r.assignedDivision &&
        r.status !== "rejected" &&
        !(r as any).invitationSentAt,
    );

  // Shows the real email for the first player of the batch, including the note
  const openInvitePreview = async (targets: SeasonRegistration[]) => {
    const sample = targets[0];
    if (!sample) return;
    setInvitePreview({ open: true, loading: true, html: "", subject: "", playerName: sample.playerName });
    try {
      const params = new URLSearchParams();
      const division = assignedDivisions[sample.id!];
      if (division) params.set("division", division);
      if (inviteNote.trim()) params.set("note", inviteNote);
      const response = await apiRequest(
        `/api/season-registration/${sample.id}/invitation-preview?${params.toString()}`,
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to build preview");
      setInvitePreview({
        open: true,
        loading: false,
        html: data.htmlBody,
        subject: data.subject,
        playerName: sample.playerName,
      });
    } catch (err: any) {
      setInvitePreview((p) => ({ ...p, open: false, loading: false }));
      setError(err.message);
    }
  };

  const renderInviteNoteEditor = (targets: SeasonRegistration[]) => (
    <Box sx={{ mt: 2 }}>
      <TextField
        label="Optional note for this batch (added at the top of each email)"
        placeholder="e.g. If you already paid and confirmed your registration, please disregard this email."
        value={inviteNote}
        onChange={(e) => setInviteNote(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
        helperText="Applies only to the emails sent now. The saved email template is not changed."
      />
      <Button
        size="small"
        sx={{ mt: 1 }}
        onClick={() => openInvitePreview(targets)}
        disabled={targets.length === 0 || invitePreview.loading}
      >
        Preview email
      </Button>
    </Box>
  );

  const handleSendTrialInvitations = async () => {
    try {
      setSendingTrialInvites(true);
      setTrialInviteResult(null);
      const targets = trialDayTargets();
      const divisionOverrides: Record<string, string> = {};
      targets.forEach((r) => {
        divisionOverrides[r.id!] = "Trial Day";
      });
      const response = await apiRequest(
        "/api/season-registration/send-invitations",
        {
          method: "POST",
          body: JSON.stringify({
            registrationIds: targets.map((r) => r.id!),
            divisionOverrides,
            seasonId: viewSeasonId,
            trialDayOnly: true,
            includeInvited: trialInviteIncludeInvited,
            customNote: inviteNote,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to send invitations");
      setTrialInviteResult({ sent: data.sent, failed: data.failed || [] });
      await fetchRegistrations(viewSeasonId);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSendingTrialInvites(false);
    }
  };

  const handleToggleConfirmationLinks = async (checked: boolean) => {
    try {
      setTogglingLinks(true);
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ confirmationLinksActive: checked }),
        ),
      });
      setConfirmationLinksActive(checked);
    } catch (err) {
      console.error("Failed to toggle confirmation links:", err);
    } finally {
      setTogglingLinks(false);
    }
  };

  const handleToggleShowPopup = async (checked: boolean) => {
    setShowPopup(checked);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(buildSettingsPayload({ showPopup: checked })),
      });
    } catch (err) {
      console.error("Failed to toggle popup visibility:", err);
    }
  };

  const handleToggleShowHeaderButton = async (checked: boolean) => {
    setShowHeaderButton(checked);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(buildSettingsPayload({ showHeaderButton: checked })),
      });
    } catch (err) {
      console.error("Failed to toggle header button visibility:", err);
    }
  };

  const handleToggleMidSeasonRegistration = async (checked: boolean) => {
    try {
      setStatusLoading(true);
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(buildSettingsPayload({ midSeasonIsOpen: checked })),
      });
      setMidSeasonIsOpen(checked);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to update mid-season registration status");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleMidSeasonPriceChange = async (newPrice: string) => {
    setMidSeasonPrice(newPrice);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ midSeasonPrice: newPrice || null }),
        ),
      });
    } catch (err) {
      console.error("Failed to update mid-season price:", err);
    }
  };

  const handleToggleMidSeasonGear = async (checked: boolean) => {
    setMidSeasonGearEnabled(checked);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ midSeasonGearEnabled: checked }),
        ),
      });
    } catch (err) {
      console.error("Failed to toggle mid-season gear:", err);
    }
  };

  const handleToggleGearEditing = async (checked: boolean) => {
    setGearEditingEnabled(checked);
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ gearEditingEnabled: checked }),
        ),
      });
    } catch (err) {
      console.error("Failed to toggle gear order editing:", err);
    }
  };

  const sizeChartSetters: Record<
    "tshirt" | "polo" | "shorts",
    (url: string) => void
  > = {
    tshirt: setTshirtSizeChartUrl,
    polo: setPoloSizeChartUrl,
    shorts: setShortsSizeChartUrl,
  };

  const handleSizeChartImageChange = async (
    category: "tshirt" | "polo" | "shorts",
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSizeChartUploading(category);
    try {
      const url = await ImageUploadService.uploadGearSizeChartImage(file);
      sizeChartSetters[category](url);
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ [`${category}SizeChartUrl`]: url }),
        ),
      });
    } catch (err: any) {
      setError(err.message || "Failed to upload size chart image");
    } finally {
      setSizeChartUploading(null);
      e.target.value = "";
    }
  };

  const handleRemoveSizeChart = async (
    category: "tshirt" | "polo" | "shorts",
  ) => {
    sizeChartSetters[category]("");
    try {
      await apiRequest("/api/season-registration/status", {
        method: "PUT",
        body: JSON.stringify(
          buildSettingsPayload({ [`${category}SizeChartUrl`]: null }),
        ),
      });
    } catch (err) {
      console.error("Failed to remove size chart:", err);
    }
  };

  const openAddDesignDialog = (category: "tshirt" | "polo" | "shorts") => {
    setEditingDesign(null);
    setDesignName("");
    setDesignImageUrl("");
    setDesignActive(true);
    setDesignCategory(category);
    setDesignDialogOpen(true);
  };

  const openEditDesignDialog = (design: GearDesign) => {
    setEditingDesign(design);
    setDesignName(design.name);
    setDesignImageUrl(design.imageUrl || "");
    setDesignActive(design.active);
    setDesignCategory(design.category || "tshirt");
    setDesignDialogOpen(true);
  };

  const handleDesignImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDesignImageUploading(true);
    try {
      const url = await ImageUploadService.uploadGearDesignImage(file);
      setDesignImageUrl(url);
    } catch (err: any) {
      setError(err.message || "Failed to upload image");
    } finally {
      setDesignImageUploading(false);
      e.target.value = "";
    }
  };

  const handleSaveDesign = async () => {
    if (!designName.trim()) return;
    setDesignSaving(true);
    try {
      const payload = {
        name: designName.trim(),
        imageUrl: designImageUrl || null,
        active: designActive,
        category: designCategory,
      };
      const response = editingDesign
        ? await apiRequest(
            `/api/season-registration/gear-designs/${editingDesign.id}`,
            { method: "PUT", body: JSON.stringify(payload) },
          )
        : await apiRequest("/api/season-registration/gear-designs", {
            method: "POST",
            body: JSON.stringify(payload),
          });
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.error || "Failed to save gear design");
      }
      setDesignDialogOpen(false);
      await fetchGearDesigns();
    } catch (err: any) {
      setError(err.message || "Failed to save gear design");
    } finally {
      setDesignSaving(false);
    }
  };

  const handleToggleDesignActive = async (design: GearDesign) => {
    setGearDesigns((prev) =>
      prev.map((d) =>
        d.id === design.id ? { ...d, active: !d.active } : d,
      ),
    );
    try {
      const response = await apiRequest(
        `/api/season-registration/gear-designs/${design.id}`,
        {
          method: "PUT",
          body: JSON.stringify({
            name: design.name,
            imageUrl: design.imageUrl || null,
            active: !design.active,
            category: design.category || "tshirt",
          }),
        },
      );
      if (!response.ok) throw new Error("Failed to update design");
    } catch (err) {
      console.error("Failed to toggle design active state:", err);
      await fetchGearDesigns();
    }
  };

  const confirmDeleteDesign = (design: GearDesign) => {
    setDesignToDelete(design);
    setDeleteDesignDialogOpen(true);
  };

  const handleDeleteDesign = async () => {
    if (!designToDelete) return;
    try {
      const response = await apiRequest(
        `/api/season-registration/gear-designs/${designToDelete.id}`,
        { method: "DELETE" },
      );
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.error || "Failed to delete gear design");
      }
      setDeleteDesignDialogOpen(false);
      setDesignToDelete(null);
      await fetchGearDesigns();
    } catch (err: any) {
      setError(err.message || "Failed to delete gear design");
    }
  };

  const renderDesignGrid = (category: "tshirt" | "polo" | "shorts") => {
    const designs = gearDesigns.filter(
      (d) => (d.category || "tshirt") === category,
    );
    if (designs.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          No{" "}
          {category === "tshirt"
            ? "t-shirt"
            : category === "polo"
              ? "polo"
              : "shorts"}{" "}
          designs yet. Click "Add Design" above to create one.
        </Alert>
      );
    }
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {designs.map((design) => (
          <Grid item xs={12} sm={6} md={4} key={design.id}>
            <Card variant="outlined">
              <Box
                sx={{
                  height: 160,
                  bgcolor: "grey.100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {design.imageUrl ? (
                  <Box
                    component="img"
                    src={design.imageUrl}
                    alt={design.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No photo
                  </Typography>
                )}
              </Box>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {design.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={design.active ? "Active" : "Inactive"}
                    color={design.active ? "success" : "default"}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={design.active}
                        onChange={() => handleToggleDesignActive(design)}
                      />
                    }
                    label="Active"
                  />
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openEditDesignDialog(design)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => confirmDeleteDesign(design)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const fetchBankDetails = async () => {
    try {
      const response = await apiRequest(
        "/api/season-registration/bank-details",
      );
      if (response.ok) {
        const data = await response.json();
        setBankIban(data.iban || "");
        setBankBic(data.bic || "");
        setBankAccountName(data.accountName || "");
        setBankBankName(data.bankName || "");
      }
    } catch (err) {
      console.error("Failed to fetch bank details:", err);
    }
  };

  // Links sent to players must always point at production, even when the admin is on localhost.
  const getPublicAppUrl = () =>
    /^(localhost|127.0.0.1)$/.test(window.location.hostname)
      ? "https://scores.stuttgart-padel.com"
      : window.location.origin;

  // Makes sure the division shown in the dropdown (possibly only a projection) is saved on the
  // registration before a link goes out, so the player's page shows it. Returns the token to use.
  const ensureDivisionSaved = async (
    registration: SeasonRegistration,
  ): Promise<string | null> => {
    const shown = assignedDivisions[registration.id!];
    if (shown && shown !== registration.assignedDivision) {
      const response = await apiRequest(
        `/api/season-registration/${registration.id}/assigned-division`,
        { method: "PUT", body: JSON.stringify({ assignedDivision: shown }) },
      );
      if (!response.ok) throw new Error("Failed to save division assignment");
      const data = await response.json();
      const token = data.confirmationToken || registration.confirmationToken || null;
      setRegistrations((prev) =>
        prev.map((r) =>
          r.id === registration.id
            ? {
                ...r,
                assignedDivision: shown,
                confirmationToken: token || r.confirmationToken,
                paymentReference: data.paymentReference || r.paymentReference,
              }
            : r,
        ),
      );
      return token;
    }
    return registration.confirmationToken || null;
  };

  const handleCopyConfirmationLink = async (registration: SeasonRegistration) => {
    let token: string | null;
    try {
      token = await ensureDivisionSaved(registration);
    } catch (err) {
      alert("Could not save the division for this player. Please try again.");
      return;
    }
    if (!token) return;
    const link = `${getPublicAppUrl()}/season-confirm/${token}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLinkId(registration.id!);
      setTimeout(() => setCopiedLinkId(null), 2000);
    });
  };

  // Opens a WhatsApp chat with this player, pre-filled with their confirmation link.
  const handleWhatsAppInvitation = async (registration: SeasonRegistration) => {
    let token: string | null;
    try {
      token = await ensureDivisionSaved(registration);
    } catch (err) {
      alert("Could not save the division for this player. Please try again.");
      return;
    }
    if (!token) return;
    let digits = (registration.phone || "").replace(/[^\d+]/g, "");
    if (digits.startsWith("+")) digits = digits.slice(1);
    else if (digits.startsWith("00")) digits = digits.slice(2);
    else if (digits.startsWith("0")) digits = "49" + digits.slice(1); // assume Germany for local numbers
    digits = digits.replace(/\D/g, "");
    if (digits.length < 8) {
      alert("This player has no valid phone number.");
      return;
    }
    const firstName = (registration.playerName || "").trim().split(/\s+/)[0] || "there";
    const link = `${getPublicAppUrl()}/season-confirm/${token}`;
    // Text comes from the editable "Whatsapp league invite" template (Email Settings);
    // the built-in text is only a fallback if the template can't be loaded.
    let template =
      "Hi {{player.firstName}},\n\n" +
      "We reached out to you through email, but here it is again to make things easier for you: the confirmation link with all the info about the upcoming league and how to pay.\n\n" +
      "Can't wait to see you on court next season.\n\n" +
      "{{confirmation.link}}";
    try {
      const response = await apiRequest("/api/email-config/templates/whatsapp_league_invite");
      if (response.ok) {
        const data = await response.json();
        if (data.template?.htmlBody?.trim()) template = data.template.htmlBody;
      }
    } catch {
      // keep the fallback text
    }
    const message = template
      .replace(/\{\{\s*player\.firstName\s*\}\}/g, firstName)
      .replace(/\{\{\s*player\.name\s*\}\}/g, (registration.playerName || "").trim() || firstName)
      .replace(/\{\{\s*confirmation\.link\s*\}\}/g, link);
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(message)}`, "_blank", "noopener");
  };

  // Copies the exact invitation email (with formatting) for one player, ready to paste into Gmail.
  const [copyingEmailId, setCopyingEmailId] = useState<string | null>(null);
  const [copiedEmailId, setCopiedEmailId] = useState<string | null>(null);
  const handleCopyInvitationEmail = async (registration: SeasonRegistration) => {
    try {
      setCopyingEmailId(registration.id!);
      const params = new URLSearchParams();
      const division = assignedDivisions[registration.id!];
      if (division) params.set("division", division);
      const response = await apiRequest(
        `/api/season-registration/${registration.id}/invitation-preview?${params.toString()}`,
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to build email");

      // Keep only the <body> content so Gmail doesn't receive a full HTML document
      const doc = new DOMParser().parseFromString(data.htmlBody, "text/html");
      const html = doc.body.innerHTML;
      const plain = doc.body.innerText || doc.body.textContent || "";

      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        // Fallback: select rendered HTML in a temporary element and copy it
        const holder = document.createElement("div");
        holder.contentEditable = "true";
        holder.style.position = "fixed";
        holder.style.left = "-9999px";
        holder.innerHTML = html;
        document.body.appendChild(holder);
        const range = document.createRange();
        range.selectNodeContents(holder);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        document.execCommand("copy");
        sel?.removeAllRanges();
        document.body.removeChild(holder);
      }
      setCopiedEmailId(registration.id!);
      setTimeout(() => setCopiedEmailId(null), 2500);
    } catch (err: any) {
      setError(err.message || "Failed to copy invitation email");
    } finally {
      setCopyingEmailId(null);
    }
  };

  const handleCopyReminderPhone = (player: { id: string; phone?: string }) => {
    if (!player.phone) return;
    navigator.clipboard.writeText(player.phone).then(() => {
      setCopiedReminderItem({ id: player.id, type: "phone" });
      setTimeout(() => setCopiedReminderItem(null), 2000);
    });
  };

  const buildRegistrationReminderMessage = (playerName: string) =>
    `Hey ${playerName}, just checking in — are you still planning to register for the next season of SPL? The registration deadline is getting close and we haven't received your registration yet. Let us know if you have any questions! 🎾`;

  const handleCopyReminderMessage = (player: { id: string; name: string }) => {
    const message = buildRegistrationReminderMessage(player.name);
    navigator.clipboard.writeText(message).then(() => {
      setCopiedReminderItem({ id: player.id, type: "message" });
      setTimeout(() => setCopiedReminderItem(null), 2000);
    });
  };

  const handleResendInvitation = async (
    registrationId: string,
    playerName: string,
  ) => {
    try {
      setResendingInvitationId(registrationId);
      const response = await apiRequest(
        `/api/season-registration/${registrationId}/resend-invitation`,
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to resend invitation");
      setEmailSuccess(data.message || `Invitation resent to ${playerName}`);
      setTimeout(() => setEmailSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setResendingInvitationId(null);
    }
  };

  const handleSendInvitations = async () => {
    try {
      setSendingInvitations(true);
      setInvitationResult(null);
      // All non-rejected registrations of this season not yet invited (saved or projected division)
      const targetRegs = registrations.filter(
        (r) =>
          assignedDivisions[r.id!] &&
          r.status !== "rejected" &&
          (inviteIncludeInvited || !(r as any).invitationSentAt),
      );
      const divisionOverrides: Record<string, string> = {};
      targetRegs.forEach((r) => {
        divisionOverrides[r.id!] = assignedDivisions[r.id!];
      });
      const response = await apiRequest(
        "/api/season-registration/send-invitations",
        {
          method: "POST",
          body: JSON.stringify({
            registrationIds: targetRegs.map((r) => r.id!),
            divisionOverrides,
            seasonId: viewSeasonId,
            includeInvited: inviteIncludeInvited,
            customNote: inviteNote,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to send invitations");
      setInvitationResult({ sent: data.sent, failed: data.failed || [] });
      // Refresh registrations to reflect invitationSentAt updates
      await fetchRegistrations(viewSeasonId);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSendingInvitations(false);
    }
  };

  const handleSendMissingInvitations = async () => {
    try {
      setSendingMissingInvitations(true);
      setMissingInvitationResult(null);
      // Collect registrations with projected/default division (not explicitly saved)
      const missingRegs = registrations.filter(
        (r) =>
          assignedDivisions[r.id!] &&
          !r.assignedDivision &&
          r.status !== "rejected" &&
          !(r as any).invitationSentAt,
      );
      const registrationIds = missingRegs.map((r) => r.id!);
      const divisionOverrides: Record<string, string> = {};
      missingRegs.forEach((r) => {
        divisionOverrides[r.id!] = assignedDivisions[r.id!];
      });
      const response = await apiRequest(
        "/api/season-registration/send-invitations",
        {
          method: "POST",
          body: JSON.stringify({
            registrationIds,
            divisionOverrides,
            seasonId: viewSeasonId,
            customNote: inviteNote,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to send invitations");
      setMissingInvitationResult({ sent: data.sent, failed: data.failed || [] });
      await fetchRegistrations(viewSeasonId);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSendingMissingInvitations(false);
    }
  };

  // Saves every division shown in the dropdowns (including projections) onto its registration,
  // so already-sent confirmation links show the division. Sends no emails.
  const handleSaveAllDivisions = async () => {
    const pending = registrations.filter(
      (r) =>
        r.status !== "rejected" &&
        assignedDivisions[r.id!] &&
        assignedDivisions[r.id!] !== r.assignedDivision,
    );
    if (pending.length === 0) {
      setEmailSuccess("All divisions are already saved.");
      setTimeout(() => setEmailSuccess(null), 4000);
      return;
    }
    try {
      setSavingDivisions(true);
      let failed = 0;
      for (const r of pending) {
        try {
          const response = await apiRequest(
            "/api/season-registration/" + r.id + "/assigned-division",
            {
              method: "PUT",
              body: JSON.stringify({ assignedDivision: assignedDivisions[r.id!] }),
            },
          );
          if (!response.ok) failed++;
        } catch {
          failed++;
        }
      }
      setEmailSuccess(
        "Saved divisions for " + (pending.length - failed) + " registration(s)" + (failed ? " (" + failed + " failed)" : ""),
      );
      setTimeout(() => setEmailSuccess(null), 5000);
      await fetchRegistrations(viewSeasonId);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingDivisions(false);
    }
  };

  const handleGenerateTokens = async () => {
    try {
      setGeneratingTokens(true);
      const response = await apiRequest(
        "/api/season-registration/generate-tokens",
        { method: "POST" },
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Failed to generate tokens");
      setEmailSuccess(
        `Tokens generated for ${data.generated} registration${data.generated !== 1 ? "s" : ""}`,
      );
      setTimeout(() => setEmailSuccess(null), 4000);
      await fetchRegistrations(viewSeasonId);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGeneratingTokens(false);
    }
  };

  const fetchCancellationPolicy = async () => {
    try {
      setPolicyLoading(true);
      const response = await apiRequest(
        "/api/season-registration/cancellation-policy",
      );
      if (response.ok) {
        const data = await response.json();
        if (data.policyLines && data.policyLines.length > 0) {
          setPolicyText(data.policyLines);
        }
      }
    } catch (err) {
      console.error("Failed to fetch cancellation policy:", err);
    } finally {
      setPolicyLoading(false);
    }
  };

  const handleSaveCancellationPolicy = async () => {
    try {
      setPolicySaving(true);
      const response = await apiRequest(
        "/api/season-registration/cancellation-policy",
        {
          method: "PUT",
          body: JSON.stringify({ policyLines: policyText }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save cancellation policy");
      }

      setPolicySuccess("Cancellation policy saved successfully!");
      setTimeout(() => setPolicySuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || "Failed to save cancellation policy");
    } finally {
      setPolicySaving(false);
    }
  };

  const handleUpdatePolicyLine = (index: number, value: string) => {
    const newPolicy = [...policyText];
    newPolicy[index] = value;
    setPolicyText(newPolicy);
  };

  const handleAddPolicyLine = () => {
    setPolicyText([...policyText, ""]);
  };

  const handleRemovePolicyLine = (index: number) => {
    if (policyText.length > 1) {
      const newPolicy = policyText.filter((_, i) => i !== index);
      setPolicyText(newPolicy);
    }
  };

  const fetchRegistrations = async (seasonId?: string) => {
    try {
      setLoading(true);
      const url = seasonId
        ? `/api/season-registration/all?seasonId=${encodeURIComponent(seasonId)}`
        : "/api/season-registration/all";
      const response = await apiRequest(url);
      if (!response.ok) {
        throw new Error("Failed to fetch registrations");
      }
      const data = await response.json();
      setRegistrations(data);

      // Initialize assigned divisions from database or use defaults
      const initialAssignments: { [registrationId: string]: string } = {};
      data.forEach((reg: SeasonRegistration) => {
        if (reg.assignedDivision) {
          // Use saved assignment
          initialAssignments[reg.id!] = reg.assignedDivision;
        } else if (reg.isExistingPlayer && reg.projectedDivision) {
          // Default for existing players: use projected division
          initialAssignments[reg.id!] = reg.projectedDivision;
        } else if (!reg.isExistingPlayer && reg.preferredDivisionLevel) {
          // Default for new players: Beginner+ → Division 3, Beginner → Division 4
          initialAssignments[reg.id!] =
            reg.preferredDivisionLevel === "beginner+"
              ? "Division 3"
              : "Division 4";
        } else {
          // Fallback
          initialAssignments[reg.id!] = "Division 4";
        }
      });
      setAssignedDivisions(initialAssignments);

      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load registrations");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await apiRequest("/api/players");
      if (!response.ok) {
        throw new Error("Failed to fetch players");
      }
      const data = await response.json();
      // Filter to only active players for accurate statistics
      // Explicitly check for isActive === true to exclude undefined and false values
      const activePlayers = data.filter((p: Player) => p.isActive === true);
      setPlayers(activePlayers);
    } catch (err: any) {
      console.error("Failed to fetch players:", err);
    }
  };

  // Calculate statistics: division-wise recurring players and new players
  const calculateStatistics = () => {
    const divisionCounts: { [division: string]: number } = {};
    let newPlayersCount = 0;
    let existingPlayersRegisteredCount = 0;
    let totalActiveNonGuestPlayers = 0;
    const liveDivisionTotals: { [division: string]: number } = {};

    // First, calculate total non-guest, active players per division from current season
    // Note: players array is already filtered to active players only in fetchPlayers()
    // Exclude Test division players (they're used for testing only)
    players.forEach((player) => {
      if (!player.isGuest && player.division !== "Test") {
        // Count all non-guest, non-test players for the overall total
        totalActiveNonGuestPlayers++;

        // Count per division only for real divisions (exclude 'unassigned')
        if (player.division && player.division !== "unassigned") {
          liveDivisionTotals[player.division] =
            (liveDivisionTotals[player.division] || 0) + 1;
        }
      }
    });

    // Set of non-guest players who submitted an existing-player registration
    // this season, regardless of which division they're requesting now.
    const registeredPlayerIds = new Set<string>();
    registrations.forEach((reg) => {
      if (reg.isExistingPlayer) {
        const player = players.find((p) => p.id === reg.playerId);
        if (player && !player.isGuest) {
          registeredPlayerIds.add(player.id);
        }
        // Always count for total registrations - we want to know everyone who registered
        existingPlayersRegisteredCount++;
      } else {
        // Count new players
        newPlayersCount++;
      }
    });

    // Use the viewed season's frozen player-count snapshot (taken when that
    // season was created as a draft) as the "last season" denominator, so it
    // doesn't drift as players get reset/reassigned during a season transition.
    // Falls back to the live count for seasons created before this snapshot existed.
    const viewedSeason = seasons.find((s) => s.id === viewSeasonId);
    const totalPlayersLastSeason =
      viewedSeason?.previousSeasonPlayerCount ?? totalActiveNonGuestPlayers;

    // Same frozen-snapshot reasoning as totalPlayersLastSeason above, but per
    // division — otherwise this would just be "everyone currently active",
    // which accumulates across every season a player has ever played, not
    // only the one immediately before the viewed season.
    const divisionTotals =
      viewedSeason?.previousSeasonDivisionCounts ?? liveDivisionTotals;

    // Count, per division, how many of that division's roster (same
    // snapshot-or-live roster used above) are in registeredPlayerIds. Mirrors
    // getDivisionDetailData so this card grid agrees with the detail dialog.
    Object.keys(divisionTotals).forEach((division) => {
      const snapshotIds =
        viewedSeason?.previousSeasonDivisionPlayerIds?.[division];
      const roster = snapshotIds
        ? players.filter((p) => !p.isGuest && snapshotIds.includes(p.id))
        : players.filter((p) => !p.isGuest && p.division === division);
      divisionCounts[division] = roster.filter((p) =>
        registeredPlayerIds.has(p.id),
      ).length;
    });

    // Total registrations = existing players registered + new players
    const totalRegistrations = existingPlayersRegisteredCount + newPlayersCount;

    return {
      divisionCounts,
      divisionTotals,
      newPlayersCount,
      totalPlayersLastSeason,
      totalRegistrations,
    };
  };

  // Calculate division planning statistics based on assigned divisions
  const calculatePlanningStatistics = () => {
    const divisionPlanCounts: { [division: string]: number } = {};
    const divisionApprovedCounts: { [division: string]: number } = {};
    const divisionOptions = [
      "Master",
      "Division 1",
      "Division 2",
      "Division 2A",
      "Division 2B",
      "Division 3",
      "Division 4",
      "Women Division",
      "Women Division 2",
      "Trial Day",
    ];

    // Initialize all divisions to 0
    divisionOptions.forEach((div) => {
      divisionPlanCounts[div] = 0;
      divisionApprovedCounts[div] = 0;
    });

    // Count ALL registrations (not just filtered ones)
    registrations.forEach((reg) => {
      const assigned = assignedDivisions[reg.id!];
      if (assigned && divisionOptions.includes(assigned)) {
        divisionPlanCounts[assigned] = (divisionPlanCounts[assigned] || 0) + 1;
        if (reg.status === "approved") {
          divisionApprovedCounts[assigned] = (divisionApprovedCounts[assigned] || 0) + 1;
        }
      }
    });

    return { divisionPlanCounts, divisionApprovedCounts };
  };

  const handleDivisionAssignmentChange = async (
    registrationId: string,
    newDivision: string,
  ) => {
    // Update local state immediately
    setAssignedDivisions((prev) => ({
      ...prev,
      [registrationId]: newDivision,
    }));

    // Save to backend
    try {
      const response = await apiRequest(
        `/api/season-registration/${registrationId}/assigned-division`,
        {
          method: "PUT",
          body: JSON.stringify({ assignedDivision: newDivision }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save division assignment");
      }

      // Backend auto-generates token + paymentReference if missing — update local state
      const data = await response.json();
      if (data.confirmationToken || data.paymentReference) {
        setRegistrations((prev) =>
          prev.map((r) =>
            r.id === registrationId
              ? {
                  ...r,
                  assignedDivision: newDivision,
                  confirmationToken:
                    data.confirmationToken || r.confirmationToken,
                  paymentReference: data.paymentReference || r.paymentReference,
                }
              : r,
          ),
        );
      }
    } catch (err) {
      console.error("Error saving division assignment:", err);
    }
  };

  const {
    divisionCounts,
    divisionTotals,
    newPlayersCount,
    totalPlayersLastSeason,
    totalRegistrations,
  } = calculateStatistics();
  const { divisionPlanCounts: planningStatistics, divisionApprovedCounts } = calculatePlanningStatistics();

  // Mutating controls (approve/reject, division edits, invitations, etc.) are
  // only enabled while browsing the season currently open for registration —
  // any other season is history and renders read-only.
  const isViewingOpenSeason =
    !!viewSeasonId && !!registrationSeasonId && viewSeasonId === registrationSeasonId;

  // Get division detail data for dialog
  const getDivisionDetailData = (division: string) => {
    // Same frozen-roster reasoning as divisionTotals in calculateStatistics:
    // use the snapshot of who was actually in this division last season,
    // rather than players' current division field, which drifts as new
    // players get assigned or existing ones get promoted/relegated.
    const viewedSeason = seasons.find((s) => s.id === viewSeasonId);
    const snapshotIds = viewedSeason?.previousSeasonDivisionPlayerIds?.[division];
    const allPlayersInDivision = snapshotIds
      ? players.filter((p) => !p.isGuest && snapshotIds.includes(p.id))
      : players.filter((p) => !p.isGuest && p.division === division);

    // registrations is already scoped to viewSeasonId, so any existing-player
    // registration from a player in this division's roster counts as
    // "re-registered" — regardless of which division they're requesting now.
    const registeredPlayerIds = new Set(
      registrations
        .filter((reg) => reg.isExistingPlayer)
        .map((reg) => reg.playerId)
        .filter((id): id is string => !!id),
    );

    const registered = allPlayersInDivision.filter((p) =>
      registeredPlayerIds.has(p.id),
    );
    const notRegistered = allPlayersInDivision.filter(
      (p) => !registeredPlayerIds.has(p.id),
    );

    return { registered, notRegistered, total: allPlayersInDivision.length };
  };

  const handleDivisionCardClick = (division: string) => {
    setSelectedDivisionForDetail(division);
    setDivisionDetailDialogOpen(true);
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    // If status is 'needs_clarification', show dialog for note
    if (newStatus === "needs_clarification") {
      setSelectedRegistrationForNote(id);
      setClarificationDialogOpen(true);
      return;
    }

    try {
      const response = await apiRequest(
        `/api/season-registration/${id}/status`,
        {
          method: "PUT",
          body: JSON.stringify({ status: newStatus }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update status");
      }

      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === id ? { ...reg, status: newStatus as any } : reg,
        ),
      );
    } catch (err: any) {
      setError(err.message || "Failed to update status");
    }
  };

  const handleClarificationSubmit = async () => {
    if (!selectedRegistrationForNote || !clarificationNote.trim()) {
      setError("Please enter a note");
      return;
    }

    try {
      const response = await apiRequest(
        `/api/season-registration/${selectedRegistrationForNote}/note`,
        {
          method: "PUT",
          body: JSON.stringify({
            adminNote: clarificationNote.trim(),
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save note");
      }

      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === selectedRegistrationForNote
            ? { ...reg, adminNote: clarificationNote.trim() }
            : reg,
        ),
      );

      setClarificationDialogOpen(false);
      setClarificationNote("");
      setSelectedRegistrationForNote(null);
    } catch (err: any) {
      setError(err.message || "Failed to save note");
    }
  };

  const handleDelete = async () => {
    if (!selectedRegistration) return;

    try {
      const response = await apiRequest(
        `/api/season-registration/${selectedRegistration}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete registration");
      }

      setRegistrations((prev) =>
        prev.filter((reg) => reg.id !== selectedRegistration),
      );
      setDeleteDialogOpen(false);
      setSelectedRegistration(null);
    } catch (err: any) {
      setError(err.message || "Failed to delete registration");
    }
  };

  const handleEditClick = (registration: SeasonRegistration) => {
    setEditingRegistration({ ...registration });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRegistration) return;

    try {
      setSavingEdit(true);
      const response = await apiRequest(
        `/api/season-registration/${editingRegistration.id}`,
        {
          method: "PUT",
          body: JSON.stringify(editingRegistration),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update registration");
      }

      // Update the local state
      setRegistrations((prev) =>
        prev.map((reg) =>
          reg.id === editingRegistration.id ? editingRegistration : reg,
        ),
      );

      setEditDialogOpen(false);
      setEditingRegistration(null);
    } catch (err: any) {
      setError(err.message || "Failed to update registration");
    } finally {
      setSavingEdit(false);
    }
  };

  const updateEditField = (field: keyof SeasonRegistration, value: any) => {
    if (!editingRegistration) return;
    setEditingRegistration({ ...editingRegistration, [field]: value });
  };

  // Gear management helpers for edit dialog
  const addEditTshirt = () => {
    if (!editingRegistration) return;
    const newTshirts = [
      ...(editingRegistration.gearTshirts || []),
      {
        size: "M" as const,
        shirtType: "tshirt" as const,
        nameOnShirt: "",
      },
    ];
    setEditingRegistration({ ...editingRegistration, gearTshirts: newTshirts });
  };

  // Designs the admin can pick from: active ones, plus the one already saved on
  // the item (it may have been deactivated since the player ordered it).
  const designsFor = (
    category: "tshirt" | "polo" | "shorts",
    selectedId?: string,
  ) =>
    gearDesigns.filter(
      (d) =>
        (d.category || "tshirt") === category &&
        (d.active || d.id === selectedId),
    );

  const updateEditTshirtDesign = (index: number, designId: string) => {
    if (!editingRegistration) return;
    const newTshirts = [...(editingRegistration.gearTshirts || [])];
    const category = newTshirts[index]?.shirtType === "polo" ? "polo" : "tshirt";
    const design = gearDesigns.find(
      (d) => d.id === designId && (d.category || "tshirt") === category,
    );
    newTshirts[index] = {
      ...newTshirts[index],
      designId: design?.id,
      designName: design?.name,
    };
    setEditingRegistration({ ...editingRegistration, gearTshirts: newTshirts });
  };

  const updateEditShortsDesign = (index: number, designId: string) => {
    if (!editingRegistration) return;
    const newShorts = [...(editingRegistration.gearShorts || [])];
    const design = gearDesigns.find(
      (d) => d.id === designId && d.category === "shorts",
    );
    newShorts[index] = {
      ...newShorts[index],
      designId: design?.id,
      designName: design?.name,
    };
    setEditingRegistration({ ...editingRegistration, gearShorts: newShorts });
  };

  const removeEditTshirt = (index: number) => {
    if (!editingRegistration) return;
    const newTshirts =
      editingRegistration.gearTshirts?.filter((_, i) => i !== index) || [];
    setEditingRegistration({ ...editingRegistration, gearTshirts: newTshirts });
  };

  const updateEditTshirt = (index: number, field: string, value: any) => {
    if (!editingRegistration) return;
    const newTshirts = [...(editingRegistration.gearTshirts || [])];
    newTshirts[index] = { ...newTshirts[index], [field]: value };
    // The design catalog is split by shirt type, so a design picked under one
    // type never carries over as a valid designId under the other.
    if (field === "shirtType") {
      delete newTshirts[index].designId;
      delete newTshirts[index].designName;
    }
    setEditingRegistration({ ...editingRegistration, gearTshirts: newTshirts });
  };

  // Each shorts comes with a pair of socks, so they are added/removed together.
  const addEditShorts = () => {
    if (!editingRegistration) return;
    setEditingRegistration({
      ...editingRegistration,
      gearShorts: [
        ...(editingRegistration.gearShorts || []),
        { size: "M" as const },
      ],
      gearSocks: [...(editingRegistration.gearSocks || []), { size: "" }],
    });
  };

  const removeEditShorts = (index: number) => {
    if (!editingRegistration) return;
    setEditingRegistration({
      ...editingRegistration,
      gearShorts:
        editingRegistration.gearShorts?.filter((_, i) => i !== index) || [],
      gearSocks:
        editingRegistration.gearSocks?.filter((_, i) => i !== index) || [],
    });
  };

  const updateEditShorts = (index: number, value: any) => {
    if (!editingRegistration) return;
    const newShorts = [...(editingRegistration.gearShorts || [])];
    newShorts[index] = { ...newShorts[index], size: value };
    setEditingRegistration({ ...editingRegistration, gearShorts: newShorts });
  };

  const updateEditSocks = (index: number, value: any) => {
    if (!editingRegistration) return;
    const newSocks = [...(editingRegistration.gearSocks || [])];
    newSocks[index] = { size: value };
    setEditingRegistration({ ...editingRegistration, gearSocks: newSocks });
  };

  const handleResendConfirmation = async (registrationId: string) => {
    try {
      setResendingEmailId(registrationId);
      const response = await apiRequest(
        `/api/season-registration/${registrationId}/resend-confirmation`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to resend confirmation email");
      }

      const data = await response.json();
      setEmailSuccess(data.message || "Confirmation email resent successfully");
      setTimeout(() => setEmailSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || "Failed to resend confirmation email");
      setTimeout(() => setError(null), 5000);
    } finally {
      setResendingEmailId(null);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedPlayerForNote) return;

    try {
      setSavingNote(true);
      const response = await apiRequest(
        `/api/players/${selectedPlayerForNote}/note`,
        {
          method: "PUT",
          body: JSON.stringify({ adminNote: playerNote }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save note");
      }

      setNoteDialogOpen(false);
      setPlayerNote("");
      setSelectedPlayerForNote(null);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleSendBulkEmail = async () => {
    if (!emailSubject || !emailMessage) {
      setError("Please enter both subject and message");
      return;
    }

    try {
      setSendingEmail(true);
      const response = await apiRequest(
        "/api/email-config/send-season-registration-bulk",
        {
          method: "POST",
          body: JSON.stringify({
            subject: emailSubject,
            message: emailMessage,
            statusFilter: emailStatusFilter,
            playerTypeFilter: emailPlayerTypeFilter,
            trialDayFilter: emailTrialDayFilter,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send emails");
      }

      const result = await response.json();
      setEmailSuccess(
        `Successfully sent ${result.sent} email(s). ${result.failed > 0 ? `Failed: ${result.failed}` : ""}`,
      );
      setEmailDialogOpen(false);
      setEmailSubject("");
      setEmailMessage("");
      setEmailStatusFilter("all");
      setEmailPlayerTypeFilter("all");
      setEmailTrialDayFilter("all");
    } catch (err: any) {
      setError(err.message || "Failed to send bulk email");
    } finally {
      setSendingEmail(false);
    }
  };

  const getFilteredRecipientsCount = () => {
    let filtered = registrations.filter((r) => r.email);

    // Apply status filter
    if (emailStatusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === emailStatusFilter);
    }

    // Apply player type filter (AND logic)
    if (emailPlayerTypeFilter !== "all") {
      if (emailPlayerTypeFilter === "new") {
        filtered = filtered.filter((r) => !r.isExistingPlayer);
      } else if (emailPlayerTypeFilter === "existing") {
        filtered = filtered.filter((r) => r.isExistingPlayer === true);
      }
    }

    // Apply trial day filter (AND logic)
    if (emailTrialDayFilter !== "all") {
      if (emailTrialDayFilter === "yes") {
        filtered = filtered.filter((r) => r.wantsTrialDay === true);
      } else if (emailTrialDayFilter === "no") {
        filtered = filtered.filter((r) => !r.wantsTrialDay);
      }
    }

    return filtered.length;
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "contacted":
        return "info";
      case "registered":
        return "success";
      case "declined":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleExportToExcel = () => {
    // Filter registrations based on selected status
    let dataToExport = registrations;
    if (exportStatusFilter !== "all") {
      dataToExport = registrations.filter(
        (r) => r.status === exportStatusFilter,
      );
    }

    // Prepare data for Excel
    const excelData = dataToExport.map((reg) => {
      // Format t-shirts list
      const tshirtsList =
        reg.gearTshirts && reg.gearTshirts.length > 0
          ? reg.gearTshirts
              .map(
                (t, i) =>
                  `${i + 1}. ${t.shirtType === "polo" ? "Polo" : "T-Shirt"}${t.designName ? ` ${t.designName}` : t.color ? ` ${t.color}` : ""} Size ${t.size}${t.nameOnShirt ? ` - "${t.nameOnShirt}"` : ""}`,
              )
              .join("; ")
          : reg.tshirtSize || "-"; // Fallback to legacy field

      // Format shorts list
      const shortsList =
        reg.gearShorts && reg.gearShorts.length > 0
          ? reg.gearShorts
              .map(
                (s, i) =>
                  `${i + 1}.${s.designName ? ` ${s.designName}` : ""} Size ${s.size}`,
              )
              .join("; ")
          : reg.shortsSize || "-"; // Fallback to legacy field

      // Format socks list
      const socksList =
        reg.gearSocks && reg.gearSocks.length > 0
          ? reg.gearSocks.map((s, i) => `${i + 1}. Size ${s.size}`).join("; ")
          : "-";

      const levelLabels: Record<string, string> = {
        beginner: "Beginner",
        "beginner+": "Beginner+",
        intermediate: "Intermediate",
        advanced: "Advanced",
        pro: "Pro",
      };

      return {
        "Player Name": reg.playerName,
        Email: reg.email,
        Phone: reg.phone,
        Gender: reg.gender || "-",
        "Player Type": reg.isExistingPlayer ? "Existing" : "New",
        "Projected Level": reg.isExistingPlayer
          ? reg.projectedDivision || "N/A"
          : levelLabels[reg.preferredDivisionLevel || "beginner"] ||
            "Beginner",
        "Level Reason": reg.levelReason || "-",
        "Playtomic Level": reg.playtomicLevel || "-",
        "Tournament Experience": reg.isExistingPlayer
          ? "-"
          : reg.hasTournamentExperience
            ? "Yes"
            : "No",
        "Heard About SPL From": reg.hearAboutUsSource || "-",
        "Reference Player": reg.hasReferencePlayer
          ? reg.referencePlayerName || "Yes"
          : "No",
        "Trial Day": reg.wantsTrialDay ? "Yes" : "No",
        "Preferred Division": reg.preferredDivision || "-",
        "Preferred Division Reason": reg.preferredDivisionReason || "-",
        "Gear Option":
          reg.gearOption === "full-kit"
            ? "Full Kit"
            : reg.gearOption === "tshirt-only"
              ? "T-Shirt Only"
              : reg.gearOption === "shorts-only"
                ? "Shorts/Skirt + Socks Only"
                : reg.wantsGear
                  ? "Yes (legacy)"
                  : "None",
        "T-Shirts": tshirtsList,
        Shorts: shortsList,
        Socks: socksList,
        "Total T-Shirts": reg.gearTshirts?.length || (reg.tshirtSize ? 1 : 0),
        "Total Shorts": reg.gearShorts?.length || (reg.shortsSize ? 1 : 0),
        "Total Socks": reg.gearSocks?.length || 0,
        "Gear Total (€)": reg.gearTotalCost || 0,
        Status: reg.status || "pending",
      };
    });

    // Create worksheet and workbook
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Registrations");

    // Generate filename
    const statusText =
      exportStatusFilter === "all" ? "All" : exportStatusFilter;
    const filename = `Season_Registrations_${statusText}_${new Date().toISOString().split("T")[0]}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);

    // Close dialog
    setExportDialogOpen(false);
  };

  const handleExportGearOrder = () => {
    // Only include registrations with gear
    const gearRegistrations = registrations.filter((r) => r.wantsGear);

    if (gearRegistrations.length === 0) {
      setError("No gear orders to export");
      return;
    }

    const sizeOrder = ["S", "M", "L", "XL", "XXL"];
    const compareSizes = (a: string, b: string) => {
      const ai = sizeOrder.indexOf(a);
      const bi = sizeOrder.indexOf(b);
      if (ai !== -1 || bi !== -1) return ai - bi;
      return Number(a) - Number(b) || a.localeCompare(b);
    };
    const capitalize = (v: string) => v.charAt(0).toUpperCase() + v.slice(1);

    // Every ordered item as one flat row; the sheets below are built from these.
    // The colour normally lives in the design name; old orders only have a
    // legacy colour field.
    type GearItem = {
      player: string;
      kind: "T-Shirt" | "Polo" | "Shorts" | "Skirt" | "Socks";
      design: string;
      size: string;
      nameOnShirt: string;
      nationality: string;
    };
    const items: GearItem[] = [];

    gearRegistrations.forEach((reg) => {
      const base = {
        player: reg.playerName,
        nationality: reg.nationality || "-",
      };

      if (reg.gearTshirts && reg.gearTshirts.length > 0) {
        reg.gearTshirts.forEach((t) =>
          items.push({
            ...base,
            kind: t.shirtType === "polo" ? "Polo" : "T-Shirt",
            design:
              t.designName || (t.color ? capitalize(t.color) : "Not specified"),
            size: t.size,
            nameOnShirt: t.nameOnShirt || "-",
          }),
        );
      } else if (reg.tshirtSize) {
        items.push({
          ...base,
          kind: "T-Shirt",
          design: "Not specified",
          size: reg.tshirtSize,
          nameOnShirt: reg.gearNameOnShirt || "-",
        });
      }

      if (reg.gearShorts && reg.gearShorts.length > 0) {
        reg.gearShorts.forEach((sh) =>
          items.push({
            ...base,
            kind: /skirt/i.test(sh.designName || "") ? "Skirt" : "Shorts",
            design: sh.designName || "Not specified",
            size: sh.size,
            nameOnShirt: "-",
          }),
        );
      } else if (reg.shortsSize) {
        items.push({
          ...base,
          kind: "Shorts",
          design: "Not specified",
          size: reg.shortsSize,
          nameOnShirt: "-",
        });
      }

      (reg.gearSocks || []).forEach((so) =>
        items.push({
          ...base,
          kind: "Socks",
          design: "-",
          size: so.size || "Not specified",
          nameOnShirt: "-",
        }),
      );
    });

    // Group identical items (type + design + size) and list who ordered them
    const groupItems = (kinds: GearItem["kind"][]) => {
      const groups = new Map<
        string,
        { kind: string; design: string; size: string; players: string[] }
      >();
      items
        .filter((i) => kinds.includes(i.kind))
        .forEach((i) => {
          const key = `${i.kind}|${i.design}|${i.size}`;
          const g = groups.get(key) || {
            kind: i.kind,
            design: i.design,
            size: i.size,
            players: [],
          };
          g.players.push(i.player);
          groups.set(key, g);
        });
      return Array.from(groups.values()).sort(
        (a, b) =>
          a.kind.localeCompare(b.kind) ||
          a.design.localeCompare(b.design) ||
          compareSizes(a.size, b.size),
      );
    };

    const workbook = XLSX.utils.book_new();
    const addSheet = (rows: any[], name: string) =>
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(rows),
        name,
      );

    // Sheet 1: Summary
    const kinds: GearItem["kind"][] = [
      "T-Shirt",
      "Polo",
      "Shorts",
      "Skirt",
      "Socks",
    ];
    addSheet(
      kinds.map((k) => ({
        Item: k,
        "Total Quantity": items.filter((i) => i.kind === k).length,
      })),
      "Summary",
    );

    // Sheet 2: T-Shirts & Polos by design and size, with who ordered them
    addSheet(
      groupItems(["T-Shirt", "Polo"]).map((g) => ({
        Type: g.kind,
        "Design / Color": g.design,
        Size: g.size,
        Quantity: g.players.length,
        Players: g.players.join(", "),
      })),
      "Shirts & Polos",
    );

    // Sheet 3: Shorts & Skirts by design and size, with who ordered them
    addSheet(
      groupItems(["Shorts", "Skirt"]).map((g) => ({
        Type: g.kind,
        "Design / Color": g.design,
        Size: g.size,
        Quantity: g.players.length,
        Players: g.players.join(", "),
      })),
      "Shorts & Skirts",
    );

    // Sheet 4: Socks by shoe size, with who ordered them
    addSheet(
      groupItems(["Socks"]).map((g) => ({
        "Shoe Size": g.size,
        Quantity: g.players.length,
        Players: g.players.join(", "),
      })),
      "Socks",
    );

    // Sheet 5: One row per ordered item, per player
    addSheet(
      items
        .slice()
        .sort(
          (a, b) =>
            a.player.localeCompare(b.player) ||
            kinds.indexOf(a.kind) - kinds.indexOf(b.kind),
        )
        .map((i) => ({
          "Player Name": i.player,
          Item: i.kind,
          "Design / Color": i.design,
          Size: i.size,
          "Name on Shirt": i.nameOnShirt,
          Nationality: i.nationality,
        })),
      "Orders by Player",
    );

    // Generate filename
    const filename = `Gear_Order_${new Date().toISOString().split("T")[0]}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Season Registrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage player registrations for the upcoming season
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {policySuccess && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setPolicySuccess(null)}
          >
            {policySuccess}
          </Alert>
        )}

        {/* Tabs */}
        <Paper elevation={3} sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: { xs: 56, sm: 48 },
                fontSize: { xs: "0.9rem", sm: "1rem" },
              },
            }}
          >
            <Tab label="Registrations" />
            <Tab label="Settings" />
            <Tab label="Gear Designs" />
          </Tabs>
        </Paper>

        {/* Tab 1: Registrations List */}
        <TabPanel value={tabValue} index={0}>
          {/* Season Viewer */}
          <Card sx={{ mb: 3 }}>
            <CardContent
              sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
            >
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="view-season-label">Viewing Season</InputLabel>
                <Select
                  labelId="view-season-label"
                  label="Viewing Season"
                  value={viewSeasonId}
                  onChange={(e) => handleViewSeasonChange(e.target.value as string)}
                >
                  {seasons.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                      {s.id === registrationSeasonId ? " (open for registration)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isViewingOpenSeason && (
                <Chip
                  label="Read-only — viewing a season that isn't open for registration"
                  color="default"
                  size="small"
                />
              )}
            </CardContent>
          </Card>

          {/* Statistics Card */}
          <Card
            sx={{
              mb: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                📊 Registration Statistics
              </Typography>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {/* Total Registrations Counter */}
                <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2.5,
                      bgcolor: "rgba(255, 255, 255, 0.95)",
                      border: "2px solid #667eea",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      📊 Total Registrations
                    </Typography>
                    <Typography variant="h2" fontWeight="bold" color="primary">
                      {totalRegistrations}/{totalPlayersLastSeason}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {totalPlayersLastSeason > 0
                        ? Math.round(
                            (totalRegistrations / totalPlayersLastSeason) * 100,
                          )
                        : 0}
                      % of last season (incl. unassigned)
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1 }}
                    >
                      ({newPlayersCount} new +{" "}
                      {totalRegistrations - newPlayersCount} returning)
                    </Typography>
                  </Paper>
                </Box>

                {/* Division Counters */}
                {Object.keys(divisionCounts).length > 0 ? (
                  <>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ bgcolor: "rgba(255, 255, 255, 0.3)" }}
                    />
                    <Box
                      sx={{
                        flex: "2 1 400px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{ width: "100%", mb: 1 }}
                      >
                        🔄 Recurring Players by Division (Non-Guest)
                      </Typography>
                      {Object.entries(divisionCounts)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([division, count]) => {
                          const total = divisionTotals[division] || 0;
                          const percentage =
                            total > 0 ? Math.round((count / total) * 100) : 0;
                          return (
                            <Box
                              key={division}
                              sx={{ flex: "1 1 140px", minWidth: 140 }}
                            >
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  bgcolor: "rgba(255, 255, 255, 0.95)",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    elevation: 4,
                                    transform: "translateY(-2px)",
                                    bgcolor: "rgba(255, 255, 255, 1)",
                                  },
                                }}
                                onClick={() =>
                                  handleDivisionCardClick(division)
                                }
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontWeight="bold"
                                  gutterBottom
                                >
                                  {division}
                                </Typography>
                                <Typography
                                  variant="h4"
                                  fontWeight="bold"
                                  color="secondary"
                                >
                                  {count}/{total}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  {percentage}% retention
                                </Typography>
                              </Paper>
                            </Box>
                          );
                        })}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ flex: "1 1 300px" }}>
                    <Paper
                      elevation={2}
                      sx={{ p: 2, bgcolor: "rgba(255, 255, 255, 0.95)" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No existing players have registered yet
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Box>
              Total shows all registrations (new + returning) vs last season's
              players. Guest players excluded from all counts
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  💡 Total: all active non-guest players (
                  {totalPlayersLastSeason} total ={" "}
                  {Object.values(divisionTotals).reduce((s, c) => s + c, 0)} in
                  divisions +{" "}
                  {totalPlayersLastSeason -
                    Object.values(divisionTotals).reduce(
                      (s, c) => s + c,
                      0,
                    )}{" "}
                  unassigned/test)
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Registration Control */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 3,
                  alignItems: "start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isRegistrationOpen}
                        onChange={(e) =>
                          handleToggleRegistration(e.target.checked)
                        }
                        disabled={statusLoading}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Registration Status:{" "}
                          {isRegistrationOpen ? "OPEN 🟢" : "CLOSED 🔴"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {isRegistrationOpen
                            ? "Players can submit registration forms"
                            : "Registration form is disabled for players"}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="registration-season-label">
                      Registration Season
                    </InputLabel>
                    <Select
                      labelId="registration-season-label"
                      label="Registration Season"
                      value={registrationSeasonId}
                      onChange={(e) =>
                        handleRegistrationSeasonChange(e.target.value as string)
                      }
                    >
                      {seasons.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    The season new registrations are filed under
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Season Name"
                    value={seasonName}
                    onChange={(e) => handleSeasonNameChange(e.target.value)}
                    fullWidth
                    size="small"
                    helperText="This will be displayed on the registration form"
                  />
                </Box>
              </Box>

              {/* Scheduled Registration Opening */}
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  ⏰ Schedule Registration Opening
                </Typography>
                <TextField
                  type="datetime-local"
                  label="Auto-Open Registration At"
                  value={registrationOpenDate}
                  onChange={(e) => handleRegistrationDateChange(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="When this date/time is reached, registration will automatically open. Leave empty to disable auto-opening."
                  sx={{ mb: 2 }}
                />
                {registrationOpenDate && !isRegistrationOpen && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Registration will automatically open on{" "}
                    {new Date(registrationOpenDate).toLocaleString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Alert>
                )}
              </Box>

              {/* Estimated Price */}
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  💰 Estimated Season Price
                </Typography>
                <TextField
                  type="number"
                  label="Estimated Price (EUR)"
                  value={estimatedPrice}
                  onChange={(e) => handleEstimatedPriceChange(e.target.value)}
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                  helperText="This will be displayed to players during registration (e.g., 165)"
                  sx={{ mb: 2 }}
                />
                {estimatedPrice && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Players will see: "Estimated price for this season will be
                    around {estimatedPrice} Euro"
                  </Alert>
                )}

                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 2, mt: 3 }}
                >
                  🎽 Gear Pricing
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Set prices for individual items and the full kit bundle.
                  Players choose between T-shirt only, shorts + socks only, or a
                  full kit.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                  <TextField
                    type="number"
                    label="T-Shirt Price (EUR)"
                    value={tshirtPrice}
                    onChange={(e) =>
                      handleGearPricesChange("tshirtPrice", e.target.value)
                    }
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Price for 1 T-shirt"
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                  <TextField
                    type="number"
                    label="Shorts/Skirt + Socks Price (EUR)"
                    value={shortsPrice}
                    onChange={(e) =>
                      handleGearPricesChange("shortsPrice", e.target.value)
                    }
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Price for 1 shorts + 1 pair of socks"
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                  <TextField
                    type="number"
                    label="Full Kit Price (EUR)"
                    value={kitPrice}
                    onChange={(e) =>
                      handleGearPricesChange("kitPrice", e.target.value)
                    }
                    inputProps={{ min: 0, step: 1 }}
                    helperText="1 T-shirt + 1 shorts + socks (bundle)"
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                </Box>
                {(tshirtPrice || shortsPrice || kitPrice) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Players will see these options:
                    {tshirtPrice && (
                      <>
                        <br />• T-Shirt only: <strong>{tshirtPrice}€</strong>
                      </>
                    )}
                    {shortsPrice && (
                      <>
                        <br />• Shorts/Skirt + Socks only:{" "}
                        <strong>{shortsPrice}€</strong>
                      </>
                    )}
                    {kitPrice && (
                      <>
                        <br />• Full Kit (T-shirt + Shorts/Skirt + Socks):{" "}
                        <strong>{kitPrice}€</strong>
                      </>
                    )}
                    <br />
                    Extra T-shirts: {tshirtPrice || "?"}€ each · Extra
                    Shorts/Skirt + Socks: {shortsPrice || "?"}€ each
                  </Alert>
                )}
              </Box>

              {/* Trial Day date */}
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                <TextField
                  label="Trial Day date"
                  value={trialDayDate}
                  onChange={(e) => setTrialDayDate(e.target.value)}
                  onBlur={(e) => saveTrialDayDate(e.target.value)}
                  placeholder="e.g. April 25 (morning)"
                  helperText="Shown in the invitation email and on the confirmation page for Trial Day players. Leave empty to omit the date."
                  fullWidth
                  size="small"
                />
              </Box>

              {/* Bank Details */}
              <BankDetailsForm
                initialIban={bankIban}
                initialBic={bankBic}
                initialAccountName={bankAccountName}
                initialBankName={bankBankName}
                onError={setError}
              />

              {/* Confirmation Links Toggle */}
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={confirmationLinksActive}
                      onChange={(e) =>
                        handleToggleConfirmationLinks(e.target.checked)
                      }
                      disabled={togglingLinks}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        Confirmation Links:{" "}
                        {confirmationLinksActive ? "ACTIVE 🟢" : "INACTIVE 🔴"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {confirmationLinksActive
                          ? "Players can open their confirmation links"
                          : "All confirmation links are disabled — players will see an error page"}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Visibility toggles */}
              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  👁️ Visibility
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Control these independently so you can open registration quietly — e.g. send the link directly to a player without advertising it publicly.
                </Typography>
                <FormControlLabel
                  sx={{ display: "flex", mb: 2 }}
                  control={
                    <Switch
                      checked={showPopup}
                      onChange={(e) => handleToggleShowPopup(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="bold">
                        Show registration popup on homepage{" "}
                        {showPopup ? "🟢" : "🔴"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {showPopup
                          ? "The \"Registration is OPEN!\" popup appears automatically for visitors"
                          : "Popup is hidden — registration is accessible only via direct link"}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  sx={{ display: "flex" }}
                  control={
                    <Switch
                      checked={showHeaderButton}
                      onChange={(e) => handleToggleShowHeaderButton(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="bold">
                        Show "Register for Next Season" button in header{" "}
                        {showHeaderButton ? "🟢" : "🔴"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {showHeaderButton
                          ? "The register button is visible in the navigation header"
                          : "Header button is hidden — registration is accessible only via direct link"}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </CardContent>
          </Card>

          {/* Mid-Season Registration */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 3,
                  alignItems: "start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={midSeasonIsOpen}
                        onChange={(e) =>
                          handleToggleMidSeasonRegistration(e.target.checked)
                        }
                        disabled={statusLoading}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          🌓 Mid-Season Registration:{" "}
                          {midSeasonIsOpen ? "OPEN 🟢" : "CLOSED 🔴"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {midSeasonIsOpen
                            ? "New players can join the remaining half of the season. This takes priority over full-season registration while open."
                            : "Lets new players join partway through the season, at a reduced price and without gear by default."}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    type="number"
                    label="Mid-Season Price (EUR)"
                    value={midSeasonPrice}
                    onChange={(e) => handleMidSeasonPriceChange(e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Flat price charged to mid-season registrants (should be less than the full-season price)"
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #e0e0e0" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={midSeasonGearEnabled}
                      onChange={(e) =>
                        handleToggleMidSeasonGear(e.target.checked)
                      }
                      color="warning"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight="bold">
                        Allow gear ordering during mid-season{" "}
                        {midSeasonGearEnabled ? "🟢" : "🔴"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {midSeasonGearEnabled
                          ? "Mid-season registrants can order t-shirts, shorts, and socks like full-season players"
                          : "Gear ordering is hidden — mid-season registrants cannot order t-shirts, shorts, or socks"}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

            </CardContent>
          </Card>

          {emailSuccess && (
            <Alert
              severity="success"
              sx={{ mb: 3 }}
              onClose={() => setEmailSuccess(null)}
            >
              {emailSuccess}
            </Alert>
          )}

          {/* Send Season Invitations */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    📧 Season Invitations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Send personalised invitation emails to all players with an
                    assigned division. Each email includes a unique confirmation
                    link.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    onClick={handleGenerateTokens}
                    disabled={generatingTokens || !isViewingOpenSeason}
                    startIcon={
                      generatingTokens ? (
                        <CircularProgress size={16} />
                      ) : (
                        <LinkIcon />
                      )
                    }
                  >
                    {generatingTokens ? "Generating..." : "Generate Tokens"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleSaveAllDivisions}
                    disabled={savingDivisions}
                    startIcon={
                      savingDivisions ? <CircularProgress size={16} /> : <CheckCircleIcon />
                    }
                  >
                    {savingDivisions ? "Saving..." : "Save All Divisions"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => {
                      setSendInvitationsDialogOpen(true);
                      setInvitationResult(null);
                    }}
                    disabled={!isViewingOpenSeason}
                    sx={{
                      background: "#1a1a2e",
                      "&:hover": { background: "#0f3460" },
                    }}
                  >
                    Send Invitations
                  </Button>
                  {registrations.filter(
                    (r) =>
                      assignedDivisions[r.id!] &&
                      !r.assignedDivision &&
                      r.status !== "rejected" &&
                      !(r as any).invitationSentAt,
                  ).length > 0 && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<SendIcon />}
                      onClick={() => {
                        setSendMissingInvitationsDialogOpen(true);
                        setMissingInvitationResult(null);
                      }}
                      disabled={!isViewingOpenSeason}
                    >
                      Send Unsent (
                      {
                        registrations.filter(
                          (r) =>
                            assignedDivisions[r.id!] &&
                            !r.assignedDivision &&
                            r.status !== "rejected" &&
                            !(r as any).invitationSentAt,
                        ).length
                      }
                      )
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={() => {
                      setTrialInviteDialogOpen(true);
                      setTrialInviteResult(null);
                    }}
                    disabled={!isViewingOpenSeason}
                  >
                    Send Trial Day Invitations
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<SendIcon />}
                    onClick={openWelcomeDialog}
                    disabled={!viewSeasonId}
                  >
                    Send Welcome Email
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<EmailIcon />}
                    onClick={() => {
                      setApologyDialogOpen(true);
                      loadApologyRecipients(apologySince);
                    }}
                  >
                    Mistaken Invitations
                  </Button>
                </Box>
              </Box>
              {invitationResult && (
                <Alert
                  severity={
                    invitationResult.failed.length === 0 ? "success" : "warning"
                  }
                  sx={{ mt: 2 }}
                >
                  ✅ {invitationResult.sent} invitation
                  {invitationResult.sent !== 1 ? "s" : ""} sent.
                  {invitationResult.failed.length > 0 && (
                    <>
                      {" "}
                      ⚠️ {invitationResult.failed.length} failed:{" "}
                      {invitationResult.failed.join(", ")}
                    </>
                  )}
                </Alert>
              )}
              {missingInvitationResult && (
                <Alert
                  severity={
                    missingInvitationResult.failed.length === 0
                      ? "success"
                      : "warning"
                  }
                  sx={{ mt: 2 }}
                >
                  ✅ {missingInvitationResult.sent} unsent invitation
                  {missingInvitationResult.sent !== 1 ? "s" : ""} sent.
                  {missingInvitationResult.failed.length > 0 && (
                    <>
                      {" "}
                      ⚠️ {missingInvitationResult.failed.length} failed:{" "}
                      {missingInvitationResult.failed.join(", ")}
                    </>
                  )}
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Tabs
                    value={statusFilter}
                    onChange={(e, newValue) => setStatusFilter(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      "& .MuiTab-root": {
                        minHeight: 42,
                        textTransform: "none",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <Tab
                      value="pending"
                      label={`Pending (${registrations.filter((r) => r.status === "pending").length})`}
                      sx={{ color: "warning.main" }}
                    />
                    <Tab
                      value="approved"
                      label={`Approved (${registrations.filter((r) => r.status === "approved").length})`}
                      sx={{ color: "success.main" }}
                    />
                    <Tab
                      value="rejected"
                      label={`Rejected (${registrations.filter((r) => r.status === "rejected").length})`}
                      sx={{ color: "error.main" }}
                    />
                    <Tab
                      value="needs_clarification"
                      label={`Needs Clarification (${registrations.filter((r) => r.status === "needs_clarification").length})`}
                      sx={{ color: "info.main" }}
                    />
                    <Tab value="all" label={`All (${registrations.length})`} />
                  </Tabs>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Showing {filteredRegistrations.length} registration
                  {filteredRegistrations.length !== 1 ? "s" : ""}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    flexWrap: "wrap",
                    mt: 1,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Preferred Level</InputLabel>
                    <Select
                      value={preferredLevelFilter}
                      onChange={(e) => setPreferredLevelFilter(e.target.value)}
                      label="Preferred Level"
                    >
                      <MenuItem value="all">All Levels</MenuItem>
                      <MenuItem value="beginner">🟢 Beginner</MenuItem>
                      <MenuItem value="beginner+">🔵 Beginner+</MenuItem>
                      <MenuItem value="intermediate">🟡 Intermediate</MenuItem>
                      <MenuItem value="advanced">🟠 Advanced</MenuItem>
                      <MenuItem value="pro">🔴 Pro</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Trial Day</InputLabel>
                    <Select
                      value={trialDayFilter}
                      onChange={(e) => setTrialDayFilter(e.target.value)}
                      label="Trial Day"
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="yes">Wants Trial Day 🏆</MenuItem>
                      <MenuItem value="no">No Trial Day</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Phase</InputLabel>
                    <Select
                      value={phaseFilter}
                      onChange={(e) => setPhaseFilter(e.target.value)}
                      label="Phase"
                    >
                      <MenuItem value="all">All Phases</MenuItem>
                      <MenuItem value="initial">Initial</MenuItem>
                      <MenuItem value="mid_season">🌓 Mid-Season</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={() => setEmailDialogOpen(true)}
                    disabled={registrations.length === 0 || !isViewingOpenSeason}
                    sx={{ ml: 2 }}
                  >
                    Send Email
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => setExportDialogOpen(true)}
                    disabled={registrations.length === 0}
                  >
                    Export to Excel
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportGearOrder}
                    disabled={
                      registrations.filter((r) => r.wantsGear).length === 0
                    }
                    sx={{ ml: 1 }}
                  >
                    Export Gear Order
                  </Button>
                </Box>
              </Box>

              {/* Division Planning Counter */}
              <Card
                sx={{
                  mb: 2,
                  mt: 2,
                  background:
                    "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                  color: "white",
                }}
              >
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    🎯 Division Planning ({registrations.length} total players)
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {Object.entries(planningStatistics).map(
                      ([division, count]) => (
                        <Paper
                          key={division}
                          elevation={2}
                          onClick={() => {
                            setSelectedPlanningDivision(division);
                            setPlanningDetailDialogOpen(true);
                          }}
                          sx={{
                            p: 2,
                            bgcolor: "rgba(255, 255, 255, 0.95)",
                            textAlign: "center",
                            minWidth: 120,
                            flex: "1 1 120px",
                            cursor: "pointer",
                            transition: "transform 0.15s, box-shadow 0.15s",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: 4,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight="bold"
                            gutterBottom
                          >
                            {division}
                          </Typography>
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            color="primary"
                          >
                            {count}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "success.main", fontWeight: "bold" }}
                          >
                            ✓ {divisionApprovedCounts[division] || 0} approved
                          </Typography>
                        </Paper>
                      ),
                    )}
                  </Box>
                </CardContent>
              </Card>

              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 1700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Date</strong>
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          bgcolor: "background.paper",
                          boxShadow: "2px 0 4px -2px rgba(0,0,0,0.2)",
                        }}
                      >
                        <strong>Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Assigned Division</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Projected Level</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Trial Day</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Email</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Phone</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Nationality</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>DPV</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Gear</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Gear Details</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Invited</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Confirmed</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Trial RSVP</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Verwendungszweck</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRegistrations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={18} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {registrations.length === 0
                              ? "No registrations yet"
                              : `No ${statusFilter} registrations`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRegistrations.map((registration) => (
                        <TableRow
                          key={registration.id}
                          hover
                          onClick={() => {
                            setSelectedRegistrationForAnswers(registration);
                            setAnswersDialogOpen(true);
                          }}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>
                            {formatDate(registration.createdAt)}
                          </TableCell>
                          <TableCell
                            sx={{
                              position: "sticky",
                              left: 0,
                              zIndex: 1,
                              bgcolor: "background.paper",
                              boxShadow: "2px 0 4px -2px rgba(0,0,0,0.2)",
                            }}
                          >
                            {registration.playerName}
                            {registration.username && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                @{registration.username}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                registration.isExistingPlayer
                                  ? "Existing Player"
                                  : "New Player"
                              }
                              color={
                                registration.isExistingPlayer
                                  ? "primary"
                                  : "success"
                              }
                              size="small"
                              variant="outlined"
                            />
                            {registration.registrationPhase === "mid_season" && (
                              <Chip
                                label="🌓 Mid-Season"
                                color="warning"
                                size="small"
                                variant="outlined"
                                sx={{ ml: 0.5, mt: 0.5 }}
                              />
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Select
                                value={
                                  assignedDivisions[registration.id!] || ""
                                }
                                onChange={(e) =>
                                  handleDivisionAssignmentChange(
                                    registration.id!,
                                    e.target.value,
                                  )
                                }
                                size="small"
                                sx={{ minWidth: 140 }}
                                disabled={!isViewingOpenSeason}
                              >
                                <MenuItem value="Master">Master</MenuItem>
                                <MenuItem value="Division 1">
                                  Division 1
                                </MenuItem>
                                <MenuItem value="Division 2">
                                  Division 2
                                </MenuItem>
                                <MenuItem value="Division 2A">
                                  Division 2A
                                </MenuItem>
                                <MenuItem value="Division 2B">
                                  Division 2B
                                </MenuItem>
                                <MenuItem value="Division 3">
                                  Division 3
                                </MenuItem>
                                <MenuItem value="Division 4">
                                  Division 4
                                </MenuItem>
                                <MenuItem value="Women Division">
                                  Women Division
                                </MenuItem>
                                <MenuItem value="Women Division 2">
                                  Women Division 2
                                </MenuItem>
                                <MenuItem value="Trial Day">Trial Day</MenuItem>
                              </Select>
                              {registration.isExistingPlayer &&
                                (() => {
                                  const player = players.find(
                                    (p) => p.id === registration.playerId,
                                  );
                                  if (player) {
                                    const gamesWon = player.gamesWon || 0;
                                    const gamesLost = player.gamesLost || 0;
                                    const balance = gamesWon - gamesLost;
                                    const isPositive = balance >= 0;

                                    return (
                                      <Chip
                                        label={`${gamesWon}/${gamesLost}`}
                                        size="small"
                                        sx={{
                                          bgcolor: isPositive
                                            ? "success.light"
                                            : "error.light",
                                          color: isPositive
                                            ? "success.dark"
                                            : "error.dark",
                                          fontWeight: "bold",
                                        }}
                                      />
                                    );
                                  }
                                  return null;
                                })()}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {registration.isExistingPlayer
                              ? // For existing players, show projected division from registration
                                (() => {
                                  // Use stored projection data from registration
                                  if (!registration.projectedDivision) {
                                    return (
                                      <Chip
                                        label={
                                          registration.wantsTrialDay
                                            ? "Trial Day 😢"
                                            : "N/A 😊"
                                        }
                                        size="small"
                                        variant="outlined"
                                      />
                                    );
                                  }

                                  const emoji = registration.wantsTrialDay
                                    ? " 😢"
                                    : " 😊";
                                  let icon = null;
                                  let color:
                                    | "success"
                                    | "warning"
                                    | "info"
                                    | "default" = "default";

                                  if (
                                    registration.projectionType === "promoted"
                                  ) {
                                    icon = <TrendingUpIcon fontSize="small" />;
                                    color = "success";
                                  } else if (
                                    registration.projectionType === "relegated"
                                  ) {
                                    icon = (
                                      <TrendingDownIcon fontSize="small" />
                                    );
                                    color = "warning";
                                  } else {
                                    icon = <RemoveIcon fontSize="small" />;
                                    color = "info";
                                  }

                                  return (
                                    <Chip
                                      icon={icon}
                                      label={`${registration.projectedDivision}${emoji}`}
                                      size="small"
                                      color={color}
                                      variant="outlined"
                                    />
                                  );
                                })()
                              : // For new players, show preferred level with emoji based on trial day
                                (() => {
                                  const emoji = registration.wantsTrialDay
                                    ? " 😢"
                                    : " 😊";
                                  const levelMap: Record<
                                    string,
                                    { label: string; color: "default" | "primary" | "warning" | "error" }
                                  > = {
                                    beginner: { label: "🟢 Beginner", color: "default" },
                                    "beginner+": { label: "🔵 Beginner+", color: "primary" },
                                    intermediate: { label: "🟡 Intermediate", color: "warning" },
                                    advanced: { label: "🟠 Advanced", color: "warning" },
                                    pro: { label: "🔴 Pro", color: "error" },
                                  };
                                  const levelInfo =
                                    levelMap[
                                      registration.preferredDivisionLevel ||
                                        "beginner"
                                    ] || levelMap.beginner;

                                  const chip = (
                                    <Chip
                                      label={`${levelInfo.label}${emoji}`}
                                      size="small"
                                      color={levelInfo.color}
                                      variant="outlined"
                                    />
                                  );

                                  return registration.levelReason ? (
                                    <Tooltip title={registration.levelReason}>
                                      {chip}
                                    </Tooltip>
                                  ) : (
                                    chip
                                  );
                                })()}
                          </TableCell>
                          <TableCell align="center">
                            {registration.wantsTrialDay ? (
                              <Box>
                                <Chip
                                  label="Yes 🏆"
                                  color="warning"
                                  size="small"
                                />
                                {registration.preferredDivision &&
                                  registration.isExistingPlayer && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      Prefers: {registration.preferredDivision}
                                    </Typography>
                                  )}
                                {registration.preferredDivisionReason &&
                                  registration.isExistingPlayer && (
                                    <Tooltip
                                      title={registration.preferredDivisionReason}
                                    >
                                      <Typography
                                        variant="caption"
                                        display="block"
                                        color="text.secondary"
                                        sx={{
                                          mt: 0.5,
                                          maxWidth: 160,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        "{registration.preferredDivisionReason}"
                                      </Typography>
                                    </Tooltip>
                                  )}
                              </Box>
                            ) : (
                              <Chip
                                label="No"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <EmailIcon fontSize="small" color="action" />
                              <a
                                href={`mailto:${registration.email}`}
                                style={{ color: "inherit" }}
                              >
                                {registration.email}
                              </a>
                            </Box>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <PhoneIcon fontSize="small" color="action" />
                              <a
                                href={`tel:${registration.phone}`}
                                style={{ color: "inherit" }}
                              >
                                {registration.phone}
                              </a>
                            </Box>
                          </TableCell>
                          <TableCell>{registration.nationality}</TableCell>
                          <TableCell align="center">
                            {registration.isDPVRegistered ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <CancelIcon color="disabled" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {registration.wantsGear ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <CancelIcon color="disabled" />
                            )}
                          </TableCell>
                          <TableCell>
                            {registration.wantsGear ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                {/* Gear Option Badge */}
                                {registration.gearOption && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: "bold",
                                      color: "primary.main",
                                    }}
                                  >
                                    {registration.gearOption === "full-kit"
                                      ? "📦 Full Kit"
                                      : registration.gearOption ===
                                          "tshirt-only"
                                        ? "👕 T-Shirt Only"
                                        : registration.gearOption ===
                                            "shorts-only"
                                          ? "🩳 Shorts/Skirt + Socks Only"
                                          : ""}
                                  </Typography>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  {/* T-Shirts */}
                                  {registration.gearTshirts &&
                                  registration.gearTshirts.length > 0 ? (
                                    <Typography variant="caption">
                                      {registration.gearTshirts
                                        .map(
                                          (t, i) =>
                                            `${t.shirtType === "polo" ? "🏇" : "👕"} ${t.shirtType === "polo" ? "Polo" : "T-Shirt"} ${t.designName ? t.designName : t.color ? (t.color === "red" ? "🔴" : t.color === "white" ? "⚪" : "⚫") + " " + t.color.charAt(0).toUpperCase() + t.color.slice(1) : ""} ${t.size}${t.nameOnShirt ? ` "${t.nameOnShirt}"` : ""}`,
                                        )
                                        .join(", ")}
                                    </Typography>
                                  ) : registration.tshirtSize ? (
                                    <Typography variant="caption">
                                      👕 {registration.tshirtSize}
                                      {registration.gearNameOnShirt
                                        ? ` "${registration.gearNameOnShirt}"`
                                        : ""}
                                    </Typography>
                                  ) : null}

                                  {/* Shorts */}
                                  {registration.gearShorts &&
                                  registration.gearShorts.length > 0 ? (
                                    <Typography variant="caption">
                                      🩳{" "}
                                      {registration.gearShorts
                                        .map((s) =>
                                          s.designName
                                            ? `${s.designName} ${s.size}`
                                            : s.size,
                                        )
                                        .join(", ")}
                                      {registration.gearShorts.length > 1 &&
                                        ` (×${registration.gearShorts.length})`}
                                    </Typography>
                                  ) : registration.shortsSize ? (
                                    <Typography variant="caption">
                                      🩳 {registration.shortsSize}
                                    </Typography>
                                  ) : null}

                                  {/* Socks */}
                                  {registration.gearSocks &&
                                    registration.gearSocks.length > 0 && (
                                      <Typography variant="caption">
                                        🧦{" "}
                                        {registration.gearSocks
                                          .map((s) => s.size)
                                          .join(", ")}
                                      </Typography>
                                    )}
                                </Box>
                                {/* Total Cost */}
                                {registration.gearTotalCost != null &&
                                  registration.gearTotalCost > 0 && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: "bold",
                                        color: "success.main",
                                      }}
                                    >
                                      💰 Gear: {registration.gearTotalCost}€
                                    </Typography>
                                  )}
                              </Box>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                No gear
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={registration.status || "pending"}
                              onChange={(e) =>
                                handleStatusChange(
                                  registration.id,
                                  e.target.value,
                                )
                              }
                              size="small"
                              sx={{ minWidth: 150 }}
                              disabled={!isViewingOpenSeason}
                            >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="approved">Approved</MenuItem>
                              <MenuItem value="rejected">Rejected</MenuItem>
                              <MenuItem value="needs_clarification">
                                Needs Clarification
                              </MenuItem>
                            </Select>
                            {registration.adminNote && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ mt: 0.5, fontStyle: "italic" }}
                              >
                                📝 {registration.adminNote}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Invited column */}
                          <TableCell align="center">
                            {(registration as any).invitationSentAt ? (
                              <Chip
                                label="✓ Sent"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ) : (
                              <Chip label="—" size="small" variant="outlined" />
                            )}
                          </TableCell>

                          {/* Confirmed column */}
                          <TableCell align="center">
                            {registration.confirmationLocked ? (
                              <Chip
                                label="✓ Confirmed"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip
                                label="Pending"
                                size="small"
                                variant="outlined"
                                color="warning"
                              />
                            )}
                          </TableCell>

                          {/* Trial RSVP column */}
                          <TableCell align="center">
                            {registration.trialDayResponse === "yes" ? (
                              <Chip
                                label="Yes ✅"
                                size="small"
                                color="success"
                              />
                            ) : registration.trialDayResponse === "no" ? (
                              <Chip
                                label="No ❌"
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* Verwendungszweck column */}
                          <TableCell>
                            {registration.paymentReference ? (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.72rem",
                                }}
                              >
                                {registration.paymentReference}
                              </Typography>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                —
                              </Typography>
                            )}
                            {registration.discountValue != null && (
                              <Chip
                                size="small"
                                color="secondary"
                                label={`🎟 ${
                                  registration.discountType === "percentage"
                                    ? `${registration.discountValue}%`
                                    : `-€${registration.discountAmount?.toFixed(2) ?? registration.discountValue}`
                                } off${registration.discountCode ? ` (${registration.discountCode})` : ""}`}
                                sx={{ display: "block", mt: 0.5, height: 18, fontSize: 10, width: "fit-content" }}
                              />
                            )}
                          </TableCell>

                          {/* Actions column */}
                          <TableCell
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                justifyContent: "center",
                              }}
                            >
                              <Tooltip
                                title={
                                  registration.confirmationEmailSent === false
                                    ? `Confirmation email failed to send${registration.confirmationEmailError ? `: ${registration.confirmationEmailError}` : ""}. Click to resend.`
                                    : registration.confirmationEmailSent === true
                                      ? `Confirmation email sent${registration.confirmationEmailSentAt ? ` on ${new Date(registration.confirmationEmailSentAt).toLocaleString()}` : ""}. Click to resend.`
                                      : "Resend confirmation email"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color={
                                      registration.confirmationEmailSent === false
                                        ? "error"
                                        : "success"
                                    }
                                    onClick={() =>
                                      handleResendConfirmation(registration.id!)
                                    }
                                    disabled={
                                      resendingEmailId === registration.id ||
                                      !isViewingOpenSeason
                                    }
                                  >
                                    {resendingEmailId === registration.id ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SendIcon />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  if (
                                    registration.isExistingPlayer &&
                                    registration.playerId
                                  ) {
                                    setSelectedPlayerForNote(
                                      registration.playerId,
                                    );
                                    setPlayerNote("");
                                    setNoteDialogOpen(true);
                                  } else {
                                    setSelectedRegistrationForNote(
                                      registration.id,
                                    );
                                    setClarificationNote(
                                      registration.adminNote || "",
                                    );
                                    setClarificationDialogOpen(true);
                                  }
                                }}
                                title={
                                  registration.isExistingPlayer
                                    ? "Add note to player"
                                    : "Add note to registration"
                                }
                                disabled={!isViewingOpenSeason}
                              >
                                <NoteIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleEditClick(registration)}
                                title="Edit registration"
                                disabled={!isViewingOpenSeason}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedRegistration(registration.id);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Delete registration"
                                disabled={!isViewingOpenSeason}
                              >
                                <DeleteIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleCopyConfirmationLink(registration)
                                }
                                title={
                                  registration.confirmationToken
                                    ? "Copy confirmation link"
                                    : "No link yet — send invitations first"
                                }
                                color={
                                  copiedLinkId === registration.id
                                    ? "success"
                                    : "default"
                                }
                                disabled={!registration.confirmationToken && !assignedDivisions[registration.id!]}
                              >
                                {copiedLinkId === registration.id ? (
                                  <CheckCircleIcon fontSize="small" />
                                ) : (
                                  <ContentCopyIcon fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() =>
                                  handleResendInvitation(
                                    registration.id!,
                                    registration.playerName,
                                  )
                                }
                                title={
                                  registration.confirmationToken
                                    ? "Resend invitation email"
                                    : "No invitation sent yet"
                                }
                                disabled={
                                  !registration.confirmationToken ||
                                  resendingInvitationId === registration.id ||
                                  !isViewingOpenSeason
                                }
                              >
                                {resendingInvitationId === registration.id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <MarkEmailReadIcon fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleCopyInvitationEmail(registration)
                                }
                                title={
                                  registration.confirmationToken
                                    ? "Copy this player's invitation email (formatted) to paste into Gmail"
                                    : "No link yet — generate tokens or send invitations first"
                                }
                                color={
                                  copiedEmailId === registration.id
                                    ? "success"
                                    : "primary"
                                }
                                disabled={
                                  !registration.confirmationToken ||
                                  copyingEmailId === registration.id
                                }
                              >
                                {copyingEmailId === registration.id ? (
                                  <CircularProgress size={18} />
                                ) : copiedEmailId === registration.id ? (
                                  <CheckCircleIcon fontSize="small" />
                                ) : (
                                  <EmailIcon fontSize="small" />
                                )}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleWhatsAppInvitation(registration)}
                                title={
                                  registration.confirmationToken
                                    ? "Open WhatsApp with this player's confirmation link"
                                    : "No link yet — send invitations first"
                                }
                                sx={{ color: "#25D366" }}
                                disabled={(!registration.confirmationToken && !assignedDivisions[registration.id!]) || !registration.phone}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
          >
            <DialogTitle>Delete Registration</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this registration? This action
                cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleDelete} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Registration Dialog */}
          <Dialog
            open={editDialogOpen}
            onClose={() => !savingEdit && setEditDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Edit Registration</DialogTitle>
            <DialogContent dividers sx={{ maxHeight: "70vh" }}>
              {editingRegistration && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <TextField
                    label="Player Name"
                    value={editingRegistration.playerName}
                    onChange={(e) =>
                      updateEditField("playerName", e.target.value)
                    }
                    fullWidth
                    required
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={editingRegistration.email}
                    onChange={(e) => updateEditField("email", e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Phone"
                    value={editingRegistration.phone}
                    onChange={(e) => updateEditField("phone", e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Nationality"
                    value={editingRegistration.nationality}
                    onChange={(e) =>
                      updateEditField("nationality", e.target.value)
                    }
                    fullWidth
                    required
                  />
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={editingRegistration.gender || ""}
                      onChange={(e) =>
                        updateEditField("gender", e.target.value)
                      }
                      label="Gender"
                    >
                      <MenuItem value="Man">Man</MenuItem>
                      <MenuItem value="Woman">Woman</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingRegistration.isDPVRegistered || false}
                        onChange={(e) =>
                          updateEditField("isDPVRegistered", e.target.checked)
                        }
                      />
                    }
                    label="DPV Registered"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingRegistration.wantsGear || false}
                        onChange={(e) =>
                          updateEditField("wantsGear", e.target.checked)
                        }
                      />
                    }
                    label="Wants Gear"
                  />
                  {editingRegistration.wantsGear && (
                    <>
                      <Divider sx={{ my: 2 }}>
                        <Chip label="Gear Details" />
                      </Divider>

                      {/* T-Shirts Section */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            👕 Shirts
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addEditTshirt}
                            variant="outlined"
                          >
                            Add Shirt
                          </Button>
                        </Box>
                        {!editingRegistration.gearTshirts ||
                        editingRegistration.gearTshirts.length === 0 ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No shirts ordered. Click "Add Shirt" to add one.
                          </Alert>
                        ) : (
                          editingRegistration.gearTshirts.map(
                            (tshirt, index) => (
                              <Box
                                key={index}
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 2,
                                  }}
                                >
                                  <ToggleButtonGroup
                                    value={tshirt.shirtType || "tshirt"}
                                    exclusive
                                    onChange={(_, val) => {
                                      if (val)
                                        updateEditTshirt(
                                          index,
                                          "shirtType",
                                          val,
                                        );
                                    }}
                                    size="small"
                                  >
                                    <ToggleButton
                                      value="tshirt"
                                      sx={{
                                        px: 2,
                                        textTransform: "none",
                                        fontWeight: 600,
                                      }}
                                    >
                                      👕 T-Shirt
                                    </ToggleButton>
                                    <ToggleButton
                                      value="polo"
                                      sx={{
                                        px: 2,
                                        textTransform: "none",
                                        fontWeight: 600,
                                      }}
                                    >
                                      🏇 Polo
                                    </ToggleButton>
                                  </ToggleButtonGroup>
                                  <IconButton
                                    onClick={() => removeEditTshirt(index)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                >
                                  <GearDesignPicker
                                    designs={designsFor(
                                      tshirt.shirtType === "polo"
                                        ? "polo"
                                        : "tshirt",
                                      tshirt.designId,
                                    )}
                                    selectedId={tshirt.designId}
                                    onSelect={(designId) =>
                                      updateEditTshirtDesign(index, designId)
                                    }
                                    label="Design"
                                    sizeChartUrl={
                                      tshirt.shirtType === "polo"
                                        ? poloSizeChartUrl
                                        : tshirtSizeChartUrl
                                    }
                                  />
                                  {!tshirt.designId && tshirt.color && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Legacy order (color: {tshirt.color}) —
                                      pick a design to update it.
                                    </Typography>
                                  )}
                                  <FormControl fullWidth>
                                    <InputLabel>Size</InputLabel>
                                    <Select
                                      value={tshirt.size}
                                      onChange={(e) =>
                                        updateEditTshirt(
                                          index,
                                          "size",
                                          e.target.value,
                                        )
                                      }
                                      label="Size"
                                      size="small"
                                    >
                                      <MenuItem value="S">S</MenuItem>
                                      <MenuItem value="M">M</MenuItem>
                                      <MenuItem value="L">L</MenuItem>
                                      <MenuItem value="XL">XL</MenuItem>
                                      <MenuItem value="XXL">XXL</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <TextField
                                    fullWidth
                                    label="Name on Shirt (Optional)"
                                    value={tshirt.nameOnShirt || ""}
                                    onChange={(e) =>
                                      updateEditTshirt(
                                        index,
                                        "nameOnShirt",
                                        e.target.value,
                                      )
                                    }
                                    size="small"
                                    inputProps={{ maxLength: 20 }}
                                  />
                                </Box>
                              </Box>
                            ),
                          )
                        )}
                      </Box>

                      {/* Shorts Section */}
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            🩳 Shorts/Skirt + Socks
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addEditShorts}
                            variant="outlined"
                          >
                            Add Shorts
                          </Button>
                        </Box>
                        {!editingRegistration.gearShorts ||
                        editingRegistration.gearShorts.length === 0 ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No shorts ordered.
                          </Alert>
                        ) : (
                          editingRegistration.gearShorts.map(
                            (shorts, index) => (
                              <Box
                                key={index}
                                sx={{
                                  mb: 2,
                                  p: 2,
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                }}
                              >
                                <GearDesignPicker
                                  designs={designsFor("shorts", shorts.designId)}
                                  selectedId={shorts.designId}
                                  onSelect={(designId) =>
                                    updateEditShortsDesign(index, designId)
                                  }
                                  label="Design"
                                  sizeChartUrl={shortsSizeChartUrl}
                                />
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <FormControl fullWidth>
                                    <InputLabel>Shorts Size</InputLabel>
                                    <Select
                                      value={shorts.size}
                                      onChange={(e) =>
                                        updateEditShorts(index, e.target.value)
                                      }
                                      label="Shorts Size"
                                      size="small"
                                    >
                                      <MenuItem value="S">S</MenuItem>
                                      <MenuItem value="M">M</MenuItem>
                                      <MenuItem value="L">L</MenuItem>
                                      <MenuItem value="XL">XL</MenuItem>
                                      <MenuItem value="XXL">XXL</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <IconButton
                                    onClick={() => removeEditShorts(index)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                                <TextField
                                  fullWidth
                                  label="🧦 Socks - Shoe Size"
                                  value={
                                    editingRegistration.gearSocks?.[index]
                                      ?.size || ""
                                  }
                                  onChange={(e) =>
                                    updateEditSocks(index, e.target.value)
                                  }
                                  size="small"
                                  placeholder="e.g., 42"
                                  inputProps={{ maxLength: 2 }}
                                  helperText="Included with shorts"
                                />
                              </Box>
                            ),
                          )
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <FormControl fullWidth>
                        <InputLabel>Gear Option</InputLabel>
                        <Select
                          value={editingRegistration.gearOption || "none"}
                          onChange={(e) =>
                            updateEditField("gearOption", e.target.value)
                          }
                          label="Gear Option"
                        >
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="tshirt-only">T-Shirt Only</MenuItem>
                          <MenuItem value="shorts-only">
                            Shorts/Skirt + Socks Only
                          </MenuItem>
                          <MenuItem value="full-kit">Full Kit</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Gear Total Cost (€)"
                        type="number"
                        value={editingRegistration.gearTotalCost || 0}
                        onChange={(e) =>
                          updateEditField(
                            "gearTotalCost",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        fullWidth
                      />
                    </>
                  )}
                  {!editingRegistration.isExistingPlayer && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Preferred Division Level</InputLabel>
                        <Select
                          value={
                            editingRegistration.preferredDivisionLevel ||
                            "beginner"
                          }
                          onChange={(e) =>
                            updateEditField(
                              "preferredDivisionLevel",
                              e.target.value,
                            )
                          }
                          label="Preferred Division Level"
                        >
                          <MenuItem value="beginner">Beginner</MenuItem>
                          <MenuItem value="beginner+">Beginner+</MenuItem>
                          <MenuItem value="intermediate">Intermediate</MenuItem>
                          <MenuItem value="advanced">Advanced</MenuItem>
                          <MenuItem value="pro">Pro</MenuItem>
                        </Select>
                      </FormControl>
                      {["intermediate", "advanced", "pro"].includes(
                        editingRegistration.preferredDivisionLevel || "",
                      ) && (
                        <TextField
                          label="Level Reason"
                          value={editingRegistration.levelReason || ""}
                          onChange={(e) =>
                            updateEditField("levelReason", e.target.value)
                          }
                          fullWidth
                          multiline
                          minRows={2}
                        />
                      )}
                      <TextField
                        label="Playtomic Level"
                        value={editingRegistration.playtomicLevel || ""}
                        onChange={(e) =>
                          updateEditField("playtomicLevel", e.target.value)
                        }
                        fullWidth
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              editingRegistration.hasTournamentExperience ||
                              false
                            }
                            onChange={(e) =>
                              updateEditField(
                                "hasTournamentExperience",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label="Has Tournament Experience"
                      />
                      <TextField
                        label="Heard About SPL From"
                        value={editingRegistration.hearAboutUsSource || ""}
                        onChange={(e) =>
                          updateEditField("hearAboutUsSource", e.target.value)
                        }
                        fullWidth
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              editingRegistration.hasReferencePlayer || false
                            }
                            onChange={(e) =>
                              updateEditField(
                                "hasReferencePlayer",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label="Has Reference Player"
                      />
                      {editingRegistration.hasReferencePlayer && (
                        <TextField
                          label="Reference Player Name"
                          value={editingRegistration.referencePlayerName || ""}
                          onChange={(e) =>
                            updateEditField(
                              "referencePlayerName",
                              e.target.value,
                            )
                          }
                          fullWidth
                        />
                      )}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editingRegistration.wantsTrialDay || false}
                            onChange={(e) =>
                              updateEditField("wantsTrialDay", e.target.checked)
                            }
                          />
                        }
                        label="Wants Trial Day"
                      />
                    </>
                  )}
                  {editingRegistration.username && (
                    <TextField
                      label="Username"
                      value={editingRegistration.username}
                      onChange={(e) =>
                        updateEditField("username", e.target.value)
                      }
                      fullWidth
                      disabled
                      helperText="Username cannot be changed"
                    />
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setEditDialogOpen(false);
                  setEditingRegistration(null);
                }}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                disabled={savingEdit}
              >
                {savingEdit ? "Saving..." : "Save Changes"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Clarification Note Dialog */}
          <Dialog
            open={clarificationDialogOpen}
            onClose={() => {
              setClarificationDialogOpen(false);
              setClarificationNote("");
              setSelectedRegistrationForNote(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add Note to Registration</DialogTitle>
            <DialogContent>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 1 }}
              >
                Add an admin note to this registration. This note is for
                internal use only.
              </Typography>
              <TextField
                label="Admin Note"
                value={clarificationNote}
                onChange={(e) => setClarificationNote(e.target.value)}
                fullWidth
                multiline
                rows={4}
                placeholder="e.g., Awaiting payment confirmation, Called on 12/25..."
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setClarificationDialogOpen(false);
                  setClarificationNote("");
                  setSelectedRegistrationForNote(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleClarificationSubmit}
                variant="contained"
                disabled={!clarificationNote.trim()}
              >
                Save Note
              </Button>
            </DialogActions>
          </Dialog>

          {/* Send Email Dialog */}
          <Dialog
            open={emailDialogOpen}
            onClose={() => !sendingEmail && setEmailDialogOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SendIcon />
                <Typography variant="h6">
                  Send Bulk Email to Registrations
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}
              >
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={emailStatusFilter}
                      onChange={(e) => setEmailStatusFilter(e.target.value)}
                      label="Filter by Status"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending Only</MenuItem>
                      <MenuItem value="approved">Approved Only</MenuItem>
                      <MenuItem value="rejected">Rejected Only</MenuItem>
                      <MenuItem value="needs_clarification">
                        Needs Clarification Only
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Player Type</InputLabel>
                    <Select
                      value={emailPlayerTypeFilter}
                      onChange={(e) => setEmailPlayerTypeFilter(e.target.value)}
                      label="Filter by Player Type"
                    >
                      <MenuItem value="all">All Players</MenuItem>
                      <MenuItem value="new">New Players Only</MenuItem>
                      <MenuItem value="existing">
                        Existing Players Only
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Trial Day</InputLabel>
                    <Select
                      value={emailTrialDayFilter}
                      onChange={(e) => setEmailTrialDayFilter(e.target.value)}
                      label="Filter by Trial Day"
                    >
                      <MenuItem value="all">All Players</MenuItem>
                      <MenuItem value="yes">Trial Day Only</MenuItem>
                      <MenuItem value="no">No Trial Day</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Alert severity="info">
                  This email will be sent to {getFilteredRecipientsCount()}{" "}
                  recipient(s)
                </Alert>

                <TextField
                  label="Email Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  fullWidth
                  placeholder="e.g., Update about Season Registration"
                  disabled={sendingEmail}
                />

                <TextField
                  label="Email Message"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Enter your message here. This will be displayed in the email body."
                  disabled={sendingEmail}
                  helperText="The player's name and season information will be automatically included in the email."
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setEmailDialogOpen(false);
                  setEmailSubject("");
                  setEmailMessage("");
                  setEmailStatusFilter("all");
                  setEmailPlayerTypeFilter("all");
                  setEmailTrialDayFilter("all");
                }}
                disabled={sendingEmail}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendBulkEmail}
                variant="contained"
                disabled={
                  sendingEmail ||
                  !emailSubject ||
                  !emailMessage ||
                  getFilteredRecipientsCount() === 0
                }
                startIcon={
                  sendingEmail ? <CircularProgress size={20} /> : <SendIcon />
                }
              >
                {sendingEmail ? "Sending..." : "Send Email"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Player Note Dialog */}
          <Dialog
            open={noteDialogOpen}
            onClose={() => {
              setNoteDialogOpen(false);
              setPlayerNote("");
              setSelectedPlayerForNote(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add Note to Player</DialogTitle>
            <DialogContent>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, mt: 1 }}
              >
                Add an administrative note for this player. This note will be
                visible in the Players page for reference.
              </Typography>
              <TextField
                label="Admin Note"
                value={playerNote}
                onChange={(e) => setPlayerNote(e.target.value)}
                fullWidth
                multiline
                rows={4}
                placeholder="e.g., Player requested to be contacted before season start"
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setNoteDialogOpen(false);
                  setPlayerNote("");
                  setSelectedPlayerForNote(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveNote}
                variant="contained"
                disabled={savingNote}
                startIcon={
                  savingNote ? <CircularProgress size={20} /> : <NoteIcon />
                }
              >
                {savingNote ? "Saving..." : "Save Note"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Export to Excel Dialog */}
          <Dialog
            open={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DownloadIcon />
                <Typography variant="h6">Export to Excel</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Select which registrations you want to export. The Excel file
                  will include:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" component="div">
                    • Player Name
                  </Typography>
                  <Typography variant="body2" component="div">
                    • T-Shirts (all items with sizes and names)
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Shorts (all items with sizes)
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Total T-Shirts count
                  </Typography>
                  <Typography variant="body2" component="div">
                    • Total Shorts count
                  </Typography>
                </Box>

                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={exportStatusFilter}
                    onChange={(e) => setExportStatusFilter(e.target.value)}
                    label="Filter by Status"
                  >
                    <MenuItem value="all">All Registrations</MenuItem>
                    <MenuItem value="pending">Pending Only</MenuItem>
                    <MenuItem value="approved">Approved Only</MenuItem>
                    <MenuItem value="rejected">Rejected Only</MenuItem>
                    <MenuItem value="needs_clarification">
                      Needs Clarification Only
                    </MenuItem>
                  </Select>
                </FormControl>

                <Alert severity="info">
                  {exportStatusFilter === "all"
                    ? `${registrations.length} registration(s) will be exported`
                    : `${registrations.filter((r) => r.status === exportStatusFilter).length} ${exportStatusFilter} registration(s) will be exported`}
                </Alert>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleExportToExcel}
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={
                  exportStatusFilter === "all"
                    ? registrations.length === 0
                    : registrations.filter(
                        (r) => r.status === exportStatusFilter,
                      ).length === 0
                }
              >
                Export
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        {/* Tab 2: Settings */}
        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                📜 Cancellation Policy Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure the cancellation policy text that players must accept
                when registering. Each line will appear as a bullet point in the
                registration form.
              </Typography>

              {policyLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {policyText.map((line, index) => (
                    <Box key={index} sx={{ mb: 2, display: "flex", gap: 1 }}>
                      <TextField
                        fullWidth
                        label={`Policy Line ${index + 1}`}
                        value={line}
                        onChange={(e) =>
                          handleUpdatePolicyLine(index, e.target.value)
                        }
                        multiline
                        rows={2}
                        variant="outlined"
                      />
                      {policyText.length > 1 && (
                        <IconButton
                          color="error"
                          onClick={() => handleRemovePolicyLine(index)}
                          sx={{ alignSelf: "flex-start", mt: 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddPolicyLine}
                    >
                      Add Policy Line
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveCancellationPolicy}
                      disabled={
                        policySaving || policyText.some((line) => !line.trim())
                      }
                    >
                      {policySaving ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Save Policy"
                      )}
                    </Button>
                  </Box>

                  {/* Preview */}
                  <Box
                    sx={{
                      mt: 4,
                      p: 2,
                      bgcolor: "warning.light",
                      borderRadius: 1,
                      border: "2px solid",
                      borderColor: "warning.main",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ color: "warning.dark" }}
                    >
                      💰 Preview: How players will see it
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: "text.primary" }}
                    >
                      Please read and accept our cancellation policy:
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ mt: 0, mb: 0, pl: 3, color: "text.primary" }}
                    >
                      {policyText
                        .filter((line) => line.trim())
                        .map((line, index) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            sx={{ mb: 1 }}
                          >
                            {line}
                          </Typography>
                        ))}
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Tab 3: Gear Designs */}
        <TabPanel value={tabValue} index={2}>
          <Card sx={{ mb: 3, border: "2px solid", borderColor: gearEditingEnabled ? "success.main" : "warning.main" }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={gearEditingEnabled}
                    onChange={(e) =>
                      handleToggleGearEditing(e.target.checked)
                    }
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="bold">
                      Allow players to modify gear orders{" "}
                      {gearEditingEnabled ? "🟢" : "🔴"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {gearEditingEnabled
                        ? "Players (and pending registrants) can change their t-shirt design, shorts, and socks selection until this is turned off"
                        : "Gear order editing is disabled — players cannot change their gear selection after submitting"}
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                🎨 Gear Design Catalog
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage the t-shirt and shorts designs players can choose from
                when ordering/editing gear.
              </Typography>

              {gearDesignsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      👕 T-Shirt Designs
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => openAddDesignDialog("tshirt")}
                    >
                      Add Design
                    </Button>
                  </Box>
                  {renderDesignGrid("tshirt")}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      🏇 Polo Designs
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => openAddDesignDialog("polo")}
                    >
                      Add Design
                    </Button>
                  </Box>
                  {renderDesignGrid("polo")}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      🩳 Shorts Designs
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => openAddDesignDialog("shorts")}
                    >
                      Add Design
                    </Button>
                  </Box>
                  {renderDesignGrid("shorts")}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                📏 Size Charts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                One size chart image per garment category. Players will be able
                to click a "View Size Chart" link next to that category's size
                selector — in registration, gear editing, and confirmation
                emails.
              </Typography>

              {(
                [
                  {
                    category: "tshirt" as const,
                    label: "👕 T-Shirt",
                    url: tshirtSizeChartUrl,
                  },
                  {
                    category: "polo" as const,
                    label: "🏇 Polo",
                    url: poloSizeChartUrl,
                  },
                  {
                    category: "shorts" as const,
                    label: "🩳 Shorts/Skirt",
                    url: shortsSizeChartUrl,
                  },
                ]
              ).map(({ category, label, url }, idx) => (
                <React.Fragment key={category}>
                  {idx > 0 && <Divider sx={{ my: 2 }} />}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1,
                    }}
                  >
                    <Avatar
                      src={url || undefined}
                      variant="rounded"
                      sx={{ width: 64, height: 64 }}
                    >
                      {!url && label.slice(0, 2)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {label} Size Chart
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {url ? "Chart uploaded" : "No chart uploaded yet"}
                      </Typography>
                    </Box>
                    <Button
                      component="label"
                      variant="outlined"
                      disabled={sizeChartUploading === category}
                      startIcon={
                        sizeChartUploading === category ? (
                          <CircularProgress size={16} />
                        ) : undefined
                      }
                    >
                      {url ? "Change" : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) =>
                          handleSizeChartImageChange(category, e)
                        }
                      />
                    </Button>
                    {url && (
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveSizeChart(category)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </React.Fragment>
              ))}
            </CardContent>
          </Card>

          {/* Add/Edit Gear Design Dialog */}
          <Dialog
            open={designDialogOpen}
            onClose={() => setDesignDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {editingDesign ? "Edit Gear Design" : "Add Gear Design"}
            </DialogTitle>
            <DialogContent>
              <ToggleButtonGroup
                value={designCategory}
                exclusive
                onChange={(_e, val) => val && setDesignCategory(val)}
                size="small"
                sx={{ mt: 2, mb: 2 }}
              >
                <ToggleButton value="tshirt">👕 T-Shirt</ToggleButton>
                <ToggleButton value="polo">🏇 Polo</ToggleButton>
                <ToggleButton value="shorts">🩳 Shorts</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                label="Design Name"
                value={designName}
                onChange={(e) => setDesignName(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" sx={{ mb: 1 }}>
                Photo (optional)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Avatar
                  variant="rounded"
                  src={designImageUrl || undefined}
                  sx={{ width: 64, height: 64 }}
                >
                  {!designImageUrl &&
                    (designCategory === "shorts"
                      ? "🩳"
                      : designCategory === "polo"
                        ? "🏇"
                        : "👕")}
                </Avatar>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={designImageUploading}
                >
                  {designImageUploading ? (
                    <CircularProgress size={18} />
                  ) : designImageUrl ? (
                    "Change Photo"
                  ) : (
                    "Upload Photo"
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleDesignImageChange}
                  />
                </Button>
                {designImageUrl && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setDesignImageUrl("")}
                  >
                    Remove
                  </Button>
                )}
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={designActive}
                    onChange={(e) => setDesignActive(e.target.checked)}
                  />
                }
                label="Active (visible to players)"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDesignDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveDesign}
                disabled={designSaving || !designName.trim()}
              >
                {designSaving ? <CircularProgress size={18} /> : "Save"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Gear Design Confirmation Dialog */}
          <Dialog
            open={deleteDesignDialogOpen}
            onClose={() => setDeleteDesignDialogOpen(false)}
          >
            <DialogTitle>Delete Gear Design</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete "{designToDelete?.name}"? This
                cannot be undone. Existing orders that reference this design
                will keep showing its name.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDesignDialogOpen(false)}>
                Cancel
              </Button>
              <Button color="error" onClick={handleDeleteDesign}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </TabPanel>

        {/* Planning Division Detail Dialog */}
        <Dialog
          open={planningDetailDialogOpen}
          onClose={() => setPlanningDetailDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleIcon />
              <Typography variant="h6">
                {selectedPlanningDivision} - Assigned Players
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedPlanningDivision &&
              (() => {
                const playersInDivision = registrations.filter(
                  (reg) =>
                    assignedDivisions[reg.id!] === selectedPlanningDivision,
                );
                const isTrialDay = selectedPlanningDivision === "Trial Day";
                const rsvpYes = isTrialDay
                  ? playersInDivision.filter((r) => r.trialDayResponse === "yes")
                  : [];
                const rsvpNo = isTrialDay
                  ? playersInDivision.filter((r) => r.trialDayResponse === "no")
                  : [];
                const rsvpPending = isTrialDay
                  ? playersInDivision.filter((r) => !r.trialDayResponse)
                  : [];

                const renderPlayerTable = (list: typeof playersInDivision, startIndex = 0) => (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>#</strong></TableCell>
                          <TableCell><strong>Player</strong></TableCell>
                          <TableCell><strong>Phone</strong></TableCell>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {list
                          .sort((a, b) => a.playerName.localeCompare(b.playerName))
                          .map((reg, index) => (
                            <TableRow key={reg.id} hover>
                              <TableCell>{startIndex + index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {reg.playerName}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {reg.phone ? (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                      {reg.phone}
                                    </Typography>
                                    <Tooltip title="Copy phone number">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleCopyReminderPhone({ id: reg.id!, phone: reg.phone })
                                        }
                                      >
                                        {copiedReminderItem?.id === reg.id &&
                                        copiedReminderItem?.type === "phone" ? (
                                          <CheckCircleIcon fontSize="small" color="success" />
                                        ) : (
                                          <ContentCopyIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    N/A
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={reg.isExistingPlayer ? "Returning" : "New"}
                                  size="small"
                                  color={reg.isExistingPlayer ? "primary" : "secondary"}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {reg.status === "approved" ? (
                                  <Chip
                                    label="Approved"
                                    size="small"
                                    color="success"
                                    icon={<span style={{ fontSize: 12, paddingLeft: 4 }}>✓</span>}
                                  />
                                ) : (
                                  <Chip
                                    label={
                                      reg.status === "pending" ? "Pending"
                                      : reg.status === "rejected" ? "Rejected"
                                      : reg.status === "needs_clarification" ? "Clarification"
                                      : reg.status || "Pending"
                                    }
                                    size="small"
                                    color={
                                      reg.status === "rejected" ? "error"
                                      : reg.status === "needs_clarification" ? "warning"
                                      : "default"
                                    }
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );

                return (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {playersInDivision.length} player
                        {playersInDivision.length !== 1 ? "s" : ""} assigned
                      </Typography>
                      <Chip
                        label={`${playersInDivision.filter(r => r.status === "approved").length} approved`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                      {isTrialDay && (
                        <>
                          <Chip label={`${rsvpYes.length} available`} size="small" color="success" />
                          <Chip label={`${rsvpNo.length} unavailable`} size="small" color="error" />
                          <Chip label={`${rsvpPending.length} no response`} size="small" color="default" variant="outlined" />
                        </>
                      )}
                    </Box>
                    {playersInDivision.length > 0 ? (
                      isTrialDay ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {rsvpYes.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="success.main" fontWeight="bold" sx={{ mb: 1 }}>
                                ✓ Available ({rsvpYes.length})
                              </Typography>
                              {renderPlayerTable(rsvpYes, 0)}
                            </Box>
                          )}
                          {rsvpNo.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="error.main" fontWeight="bold" sx={{ mb: 1 }}>
                                ✗ Not Available ({rsvpNo.length})
                              </Typography>
                              {renderPlayerTable(rsvpNo, 0)}
                            </Box>
                          )}
                          {rsvpPending.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" fontWeight="bold" sx={{ mb: 1 }}>
                                ? No Response Yet ({rsvpPending.length})
                              </Typography>
                              {renderPlayerTable(rsvpPending, 0)}
                            </Box>
                          )}
                          {rsvpYes.length === 0 && rsvpNo.length === 0 && rsvpPending.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No players assigned to this division yet.
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        renderPlayerTable(playersInDivision, 0)
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No players assigned to this division yet.
                      </Typography>
                    )}
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlanningDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Division Detail Dialog */}
        <Dialog
          open={divisionDetailDialogOpen}
          onClose={() => setDivisionDetailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleIcon />
              <Typography variant="h6">
                {selectedDivisionForDetail} - Registration Status
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedDivisionForDetail &&
              (() => {
                const { registered, notRegistered, total } =
                  getDivisionDetailData(selectedDivisionForDetail);
                const percentage =
                  total > 0 ? Math.round((registered.length / total) * 100) : 0;

                return (
                  <Box sx={{ mt: 2 }}>
                    {/* Summary */}
                    <Paper
                      sx={{
                        p: 2,
                        mb: 3,
                        bgcolor: "primary.light",
                        color: "white",
                      }}
                    >
                      <Typography variant="h4" fontWeight="bold" gutterBottom>
                        {registered.length}/{total}
                      </Typography>
                      <Typography variant="body1">
                        {percentage}% of players have re-registered
                      </Typography>
                    </Paper>

                    {/* Registered Players */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CheckCircleIcon color="success" />
                        Re-Registered ({registered.length})
                      </Typography>
                      {registered.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {registered
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((player) => (
                              <Chip
                                key={player.id}
                                label={player.name}
                                color="success"
                                variant="outlined"
                                size="small"
                              />
                            ))}
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          No players have re-registered yet
                        </Typography>
                      )}
                    </Box>

                    {/* Not Registered Players */}
                    <Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CancelIcon color="error" />
                        Not Yet Registered ({notRegistered.length})
                      </Typography>
                      {notRegistered.length > 0 ? (
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          sx={{ mt: 2 }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <strong>Player</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Email</strong>
                                </TableCell>
                                <TableCell>
                                  <strong>Phone</strong>
                                </TableCell>
                                <TableCell align="center">
                                  <strong>Remind</strong>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {notRegistered
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((player) => (
                                  <TableRow key={player.id} hover>
                                    <TableCell>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1.5,
                                        }}
                                      >
                                        {player.profilePicture ? (
                                          <Box
                                            component="img"
                                            src={player.profilePicture}
                                            alt={player.name}
                                            sx={{
                                              width: 40,
                                              height: 40,
                                              borderRadius: "50%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        ) : (
                                          <Box
                                            sx={{
                                              width: 40,
                                              height: 40,
                                              borderRadius: "50%",
                                              bgcolor: "primary.main",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              color: "white",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            {player.name
                                              .charAt(0)
                                              .toUpperCase()}
                                          </Box>
                                        )}
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                        >
                                          {player.name}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      {player.email ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <EmailIcon
                                            fontSize="small"
                                            color="action"
                                          />
                                          <a
                                            href={`mailto:${player.email}`}
                                            style={{
                                              color: "inherit",
                                              textDecoration: "none",
                                            }}
                                          >
                                            {player.email}
                                          </a>
                                        </Box>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          N/A
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {player.phone ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                          }}
                                        >
                                          <PhoneIcon
                                            fontSize="small"
                                            color="action"
                                          />
                                          <a
                                            href={`tel:${player.phone}`}
                                            style={{
                                              color: "inherit",
                                              textDecoration: "none",
                                            }}
                                          >
                                            {player.phone}
                                          </a>
                                          <Tooltip title="Copy phone number">
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleCopyReminderPhone(player)
                                              }
                                            >
                                              {copiedReminderItem?.id ===
                                                player.id &&
                                              copiedReminderItem?.type ===
                                                "phone" ? (
                                                <CheckCircleIcon
                                                  fontSize="small"
                                                  color="success"
                                                />
                                              ) : (
                                                <ContentCopyIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          N/A
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="Copy WhatsApp reminder message">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleCopyReminderMessage(player)
                                          }
                                        >
                                          {copiedReminderItem?.id ===
                                            player.id &&
                                          copiedReminderItem?.type ===
                                            "message" ? (
                                            <CheckCircleIcon
                                              fontSize="small"
                                              color="success"
                                            />
                                          ) : (
                                            <WhatsAppIcon
                                              fontSize="small"
                                              sx={{ color: "#25D366" }}
                                            />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          All players have re-registered! 🎉
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDivisionDetailDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Registration Answers Dialog */}
        <Dialog
          open={answersDialogOpen}
          onClose={() => setAnswersDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleIcon />
              <Typography variant="h6">
                {selectedRegistrationForAnswers?.playerName} - Registration
                Answers
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedRegistrationForAnswers &&
              (() => {
                const reg = selectedRegistrationForAnswers;

                const Field = ({
                  label,
                  value,
                }: {
                  label: string;
                  value: React.ReactNode;
                }) =>
                  value === null || value === undefined || value === "" ? null : (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {label}
                      </Typography>
                      <Typography variant="body2">{value}</Typography>
                    </Box>
                  );

                const levelLabels: Record<string, string> = {
                  beginner: "🟢 Beginner",
                  "beginner+": "🔵 Beginner+",
                  intermediate: "🟡 Intermediate",
                  advanced: "🟠 Advanced",
                  pro: "🔴 Pro",
                };

                return (
                  <Box>
                    <Field label="Player Name" value={reg.playerName} />
                    <Field label="Email" value={reg.email} />
                    <Field label="Phone" value={reg.phone} />
                    <Field label="Nationality" value={reg.nationality} />
                    <Field label="Gender" value={reg.gender} />

                    <Divider sx={{ my: 2 }} />

                    {reg.isExistingPlayer ? (
                      <>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          Existing Player Answers
                        </Typography>
                        <Field
                          label="Which division do you think you should play in?"
                          value={reg.preferredDivision}
                        />
                        <Field
                          label={`Why I think I should play in ${reg.preferredDivision || "this division"}`}
                          value={reg.preferredDivisionReason}
                        />
                        <Field
                          label="I would like to play in the trial day to prove I should play in another division"
                          value={reg.wantsTrialDay ? "Yes 🏆" : "No"}
                        />
                        <Field
                          label="Projected Division"
                          value={reg.projectedDivision}
                        />
                        <Field
                          label="Current Division"
                          value={reg.currentDivision}
                        />
                      </>
                    ) : (
                      <>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          gutterBottom
                        >
                          New Player Answers
                        </Typography>
                        <Field
                          label="Which level would you prefer?"
                          value={
                            levelLabels[reg.preferredDivisionLevel || ""] ||
                            reg.preferredDivisionLevel
                          }
                        />
                        <Field
                          label={`Why do you think you should be placed at ${reg.preferredDivisionLevel || "this"} level?`}
                          value={reg.levelReason}
                        />
                        <Field
                          label="Playtomic Level"
                          value={reg.playtomicLevel}
                        />
                        <Field
                          label="Did you participate in any tournaments?"
                          value={
                            reg.hasTournamentExperience === true
                              ? "Yes"
                              : reg.hasTournamentExperience === false
                                ? "No"
                                : undefined
                          }
                        />
                        <Field
                          label="Where did you hear about the Stuttgart Padel League from?"
                          value={reg.hearAboutUsSource}
                        />
                        <Field
                          label="Do you play regularly with a player from SPL that we can ask your level from?"
                          value={
                            reg.hasReferencePlayer === true
                              ? "Yes"
                              : reg.hasReferencePlayer === false
                                ? "No"
                                : undefined
                          }
                        />
                        <Field
                          label="Name of the SPL player who can vouch for your level"
                          value={reg.referencePlayerName}
                        />
                        <Field
                          label="Okay to participate in a trial day?"
                          value={reg.wantsTrialDay ? "Yes 🏆" : "No"}
                        />
                      </>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Gear & Other
                    </Typography>
                    <Field
                      label="Registered with DPV"
                      value={reg.isDPVRegistered ? "Yes" : "No"}
                    />
                    <Field
                      label="Wants Gear"
                      value={reg.wantsGear ? "Yes" : "No"}
                    />
                    <Field
                      label="Gear Option"
                      value={
                        reg.gearOption === "full-kit"
                          ? "📦 Full Kit"
                          : reg.gearOption === "tshirt-only"
                            ? "👕 T-Shirt Only"
                            : reg.gearOption === "shorts-only"
                              ? "🩳 Shorts/Skirt + Socks Only"
                              : reg.gearOption
                      }
                    />
                    <Field
                      label="T-Shirts"
                      value={
                        reg.gearTshirts && reg.gearTshirts.length > 0
                          ? reg.gearTshirts
                              .map(
                                (t) =>
                                  `${t.shirtType === "polo" ? "🏇 Polo" : "👕 T-Shirt"} — ${
                                    t.designName
                                      ? t.designName
                                      : t.color
                                        ? t.color.charAt(0).toUpperCase() +
                                          t.color.slice(1)
                                        : "No design selected"
                                  } — Size ${t.size}${t.nameOnShirt ? ` — "${t.nameOnShirt}"` : ""}`,
                              )
                              .join("; ")
                          : reg.tshirtSize
                            ? `👕 Size ${reg.tshirtSize}${reg.gearNameOnShirt ? ` — "${reg.gearNameOnShirt}"` : ""}`
                            : undefined
                      }
                    />
                    <Field
                      label="Shorts/Skirt"
                      value={
                        reg.gearShorts && reg.gearShorts.length > 0
                          ? reg.gearShorts
                              .map(
                                (s) =>
                                  `🩳 ${s.designName || "No design selected"} — Size ${s.size}`,
                              )
                              .join("; ")
                          : reg.shortsSize
                            ? `🩳 Size ${reg.shortsSize}`
                            : undefined
                      }
                    />
                    <Field
                      label="Socks"
                      value={
                        reg.gearSocks && reg.gearSocks.length > 0
                          ? reg.gearSocks
                              .map((s) => `🧦 Shoe size ${s.size}`)
                              .join("; ")
                          : undefined
                      }
                    />
                    <Field
                      label="Voucher Code"
                      value={reg.voucherCode || reg.discountCode}
                    />
                    <Field
                      label="Admin Note"
                      value={reg.adminNote}
                    />
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAnswersDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Send Invitations Dialog */}
        <Dialog
          open={sendInvitationsDialogOpen}
          onClose={() =>
            !sendingInvitations && setSendInvitationsDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>📧 Send Season Invitations</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will send a personalised invitation email to all
              registrations of this season that have an{" "}
              <strong>assigned division</strong>, are not rejected and have not
              been invited yet. Each email contains a unique confirmation link.
            </Typography>
            {!confirmationLinksActive && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                ⚠️ Confirmation links are currently <strong>inactive</strong>.
                Enable them in the settings above before sending invitations.
              </Alert>
            )}
            {(!bankIban || !bankAccountName) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                ⚠️ Bank details are not fully configured. Please save IBAN, BIC,
                and account name first.
              </Alert>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={inviteIncludeInvited}
                  onChange={(e) => setInviteIncludeInvited(e.target.checked)}
                />
              }
              label="Override: also re-send to players who were already invited"
            />
            {inviteIncludeInvited && (
              <Alert severity="warning" sx={{ my: 1 }}>
                Players who already received an invitation will get it again.
                Only use this if their earlier email failed to send.
              </Alert>
            )}
            {renderInviteNoteEditor(mainInviteTargets())}
            <Typography variant="body2">
              Invitations to send:{" "}
              <strong>
                {
                  registrations.filter(
                    (r) =>
                      assignedDivisions[r.id!] &&
                      r.status !== "rejected" &&
                      (inviteIncludeInvited || !(r as any).invitationSentAt),
                  ).length
                }
              </strong>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              (
              {
                registrations.filter(
                  (r) => r.assignedDivision && r.status !== "rejected",
                ).length
              }{" "}
              explicitly saved,{" "}
              {
                registrations.filter(
                  (r) =>
                    assignedDivisions[r.id!] &&
                    !r.assignedDivision &&
                    r.status !== "rejected",
                ).length
              }{" "}
              using projected/default)
            </Typography>
            {invitationResult && (
              <Alert
                severity={
                  invitationResult.failed.length === 0 ? "success" : "warning"
                }
                sx={{ mt: 2 }}
              >
                ✅ {invitationResult.sent} sent.
                {invitationResult.failed.length > 0 && (
                  <> ⚠️ Failed: {invitationResult.failed.join(", ")}</>
                )}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setSendInvitationsDialogOpen(false)}
              disabled={sendingInvitations}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendInvitations}
              disabled={
                sendingInvitations ||
                !confirmationLinksActive ||
                !bankIban ||
                !bankAccountName
              }
              startIcon={
                sendingInvitations ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{ background: "#1a1a2e" }}
            >
              {sendingInvitations ? "Sending..." : "Send Now"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send Missing Invitations Dialog */}
        <Dialog
          open={sendMissingInvitationsDialogOpen}
          onClose={() =>
            !sendingMissingInvitations &&
            setSendMissingInvitationsDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>📧 Send Unsent Invitations</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will send invitation emails <strong>only</strong> to the
              registrations that have a projected/default division but did{" "}
              <strong>not</strong> receive an email in the bulk send. Their
              division will be resolved from the projected value.
            </Typography>
            <Typography variant="body2">
              Registrations to send to:{" "}
              <strong>
                {
                  registrations.filter(
                    (r) =>
                      assignedDivisions[r.id!] &&
                      !r.assignedDivision &&
                      r.status !== "rejected" &&
                      !(r as any).invitationSentAt,
                  ).length
                }
              </strong>
            </Typography>
            {renderInviteNoteEditor(missingInviteTargets())}
            {missingInvitationResult && (
              <Alert
                severity={
                  missingInvitationResult.failed.length === 0
                    ? "success"
                    : "warning"
                }
                sx={{ mt: 2 }}
              >
                ✅ {missingInvitationResult.sent} sent.
                {missingInvitationResult.failed.length > 0 && (
                  <>
                    {" "}
                    ⚠️ Failed: {missingInvitationResult.failed.join(", ")}
                  </>
                )}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setSendMissingInvitationsDialogOpen(false)}
              disabled={sendingMissingInvitations}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleSendMissingInvitations}
              disabled={sendingMissingInvitations}
              startIcon={
                sendingMissingInvitations ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {sendingMissingInvitations ? "Sending..." : "Send Now"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Welcome Email Dialog */}
        <Dialog
          open={welcomeDialogOpen}
          onClose={() => !welcomeSending && setWelcomeDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>👋 Send Welcome Email</DialogTitle>
          <DialogContent dividers>
            {welcomeLoading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Approved players of <strong>this season only</strong> who got a
                  new account. Nothing is sent until you press Send. Players who
                  already received the welcome email are unticked.
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 240, mb: 3 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={
                              welcomeRecipients.length > 0 &&
                              welcomeSelected.size === welcomeRecipients.length
                            }
                            indeterminate={
                              welcomeSelected.size > 0 &&
                              welcomeSelected.size < welcomeRecipients.length
                            }
                            onChange={(e) =>
                              setWelcomeSelected(
                                e.target.checked
                                  ? new Set(welcomeRecipients.map((r) => r.id))
                                  : new Set(),
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>Player</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Division</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {welcomeRecipients.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            No approved new players in this season.
                          </TableCell>
                        </TableRow>
                      )}
                      {welcomeRecipients.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={welcomeSelected.has(r.id)}
                              onChange={(e) =>
                                setWelcomeSelected((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(r.id);
                                  else next.delete(r.id);
                                  return next;
                                })
                              }
                            />
                          </TableCell>
                          <TableCell>{r.playerName}</TableCell>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>{r.username}</TableCell>
                          <TableCell>{r.division}</TableCell>
                          <TableCell>
                            {r.welcomeEmailSentAt ? (
                              <Chip
                                size="small"
                                color="success"
                                label={`Sent ${new Date(r.welcomeEmailSentAt).toLocaleDateString()}`}
                              />
                            ) : (
                              <Chip size="small" label="Not sent" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Will NOT receive the email ({welcomeNotApproved.length})
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  New players of this season who are not approved yet or were rejected.
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 200, mb: 3 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Player</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {welcomeNotApproved.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            Everyone who signed up is approved.
                          </TableCell>
                        </TableRow>
                      )}
                      {welcomeNotApproved.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.playerName}</TableCell>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>{r.username}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={r.status === "rejected" ? "error" : "warning"}
                              label={
                                r.status === "needs_clarification"
                                  ? "Needs clarification"
                                  : r.status.charAt(0).toUpperCase() + r.status.slice(1)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TextField
                  label="Subject"
                  size="small"
                  fullWidth
                  value={welcomeSubject}
                  onChange={(e) => setWelcomeSubject(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Message (HTML — {{playerName}}, {{username}} and {{division}} are replaced per player)"
                  fullWidth
                  multiline
                  minRows={6}
                  value={welcomeBody}
                  onChange={(e) => setWelcomeBody(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Preview{welcomeSample ? ` (as ${welcomeSample.playerName} would see it)` : ""}
                  {" — "}Subject: {fillWelcome(welcomeSubject)}
                </Typography>
                <iframe
                  title="Welcome email preview"
                  srcDoc={fillWelcome(welcomeBody)}
                  sandbox=""
                  style={{ width: "100%", height: 380, border: "1px solid #ddd", borderRadius: 4 }}
                />

                {welcomeResult && (
                  <Alert
                    severity={welcomeResult.failed.length === 0 ? "success" : "warning"}
                    sx={{ mt: 2 }}
                  >
                    ✅ {welcomeResult.sent} sent.
                    {welcomeResult.failed.length > 0 && (
                      <> ⚠️ Failed: {welcomeResult.failed.join(", ")}</>
                    )}
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setWelcomeDialogOpen(false)}
              disabled={welcomeSending}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSendWelcomeEmails}
              disabled={
                welcomeSending ||
                welcomeLoading ||
                welcomeSelected.size === 0 ||
                !welcomeSubject.trim() ||
                !welcomeBody.trim() ||
                !!welcomeResult
              }
              startIcon={
                welcomeSending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {welcomeSending ? "Sending..." : `Send to ${welcomeSelected.size}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email preview dialog */}
        <Dialog
          open={invitePreview.open}
          onClose={() => setInvitePreview((p) => ({ ...p, open: false }))}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Email preview</DialogTitle>
          <DialogContent dividers>
            {invitePreview.loading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Sample for <strong>{invitePreview.playerName}</strong>. Each
                  player gets their own division, link and payment details.
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Subject: {invitePreview.subject}
                </Typography>
                <iframe
                  title="Email preview"
                  srcDoc={invitePreview.html}
                  sandbox=""
                  style={{ width: "100%", height: 520, border: "1px solid #ddd", borderRadius: 4 }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInvitePreview((p) => ({ ...p, open: false }))}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Trial Day Invitations Dialog */}
        <Dialog
          open={trialInviteDialogOpen}
          onClose={() => !sendingTrialInvites && setTrialInviteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>🎾 Send Trial Day Invitations</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sends the invitation email only to players whose division is{" "}
              <strong>Trial Day</strong>. The email uses the Trial Day date:{" "}
              <strong>{trialDayDate || "(not set - no date will be shown)"}</strong>
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={trialInviteIncludeInvited}
                  onChange={(e) => setTrialInviteIncludeInvited(e.target.checked)}
                />
              }
              label="Also re-send to players who were already invited"
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Players to email: <strong>{trialDayTargets().length}</strong>
            </Typography>
            {renderInviteNoteEditor(trialDayTargets())}
            {trialInviteResult && (
              <Alert
                severity={trialInviteResult.failed.length === 0 ? "success" : "warning"}
                sx={{ mt: 2 }}
              >
                ✅ {trialInviteResult.sent} sent.
                {trialInviteResult.failed.length > 0 && (
                  <> ⚠️ Failed: {trialInviteResult.failed.join(", ")}</>
                )}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setTrialInviteDialogOpen(false)}
              disabled={sendingTrialInvites}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSendTrialInvitations}
              disabled={sendingTrialInvites || trialDayTargets().length === 0}
              startIcon={
                sendingTrialInvites ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {sendingTrialInvites ? "Sending..." : "Send Now"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mistaken Invitations Dialog */}
        <Dialog
          open={apologyDialogOpen}
          onClose={() => !apologySending && setApologyDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>⚠️ Invitations Sent By Mistake</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Registrations from <strong>other seasons</strong> that received an
              invitation email after the time below. They are split in two
              groups, each with its own message. Each address gets one email,
              even if it appears more than once.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
              <TextField
                label="Sent since"
                type="datetime-local"
                size="small"
                value={apologySince}
                onChange={(e) => setApologySince(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                onClick={() => loadApologyRecipients(apologySince)}
                disabled={apologyLoading}
              >
                Refresh
              </Button>
            </Box>
            {apologyLoading && <CircularProgress size={24} />}
            {!apologyLoading &&
              (["apology", "duplicate"] as ApologyGroup[]).map((group) => {
                const list = groupRecipients(group);
                const isApology = group === "apology";
                const subject = isApology ? apologySubject : duplicateSubject;
                const body = isApology ? apologyBody : duplicateBody;
                const result = isApology ? apologyResult : duplicateResult;
                return (
                  <Box key={group} sx={{ mb: 3 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {isApology
                        ? "1. Not registered this season - apology"
                        : "2. Registered this season - received two emails"}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {list.length} registration{list.length !== 1 ? "s" : ""} (
                      {uniqueEmailCount(list)} unique emails)
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 200, mb: 2 }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Player</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Sent</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {list.map((r) => (
                            <TableRow key={r.id}>
                              <TableCell>{r.playerName}</TableCell>
                              <TableCell>{r.email}</TableCell>
                              <TableCell>
                                {new Date(r.invitationSentAt).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TextField
                      label="Subject"
                      size="small"
                      fullWidth
                      value={subject}
                      onChange={(e) =>
                        isApology
                          ? setApologySubject(e.target.value)
                          : setDuplicateSubject(e.target.value)
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      label="Message (HTML, {{playerName}} is replaced)"
                      fullWidth
                      multiline
                      minRows={5}
                      value={body}
                      onChange={(e) =>
                        isApology
                          ? setApologyBody(e.target.value)
                          : setDuplicateBody(e.target.value)
                      }
                    />
                    <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleSendApology(group)}
                        disabled={
                          !!apologySending ||
                          list.length === 0 ||
                          !subject.trim() ||
                          !body.trim() ||
                          !!result
                        }
                        startIcon={
                          apologySending === group ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SendIcon />
                          )
                        }
                      >
                        {apologySending === group
                          ? "Sending..."
                          : `Send to ${uniqueEmailCount(list)}`}
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        border: "1px dashed #bbb",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Send manually (Gmail)
                      </Typography>
                      <TextField
                        label={`Recipients (${uniqueEmailCount(list)})`}
                        value={groupEmailList(group)}
                        fullWidth
                        multiline
                        maxRows={4}
                        size="small"
                        InputProps={{ readOnly: true }}
                        onFocus={(e) => e.target.select()}
                        sx={{ mb: 1 }}
                      />
                      <TextField
                        label="Message (plain text)"
                        value={htmlToPlainText(body)}
                        fullWidth
                        multiline
                        minRows={4}
                        size="small"
                        InputProps={{ readOnly: true }}
                        onFocus={(e) => e.target.select()}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          disabled={list.length === 0}
                          onClick={() =>
                            copyText(`${group}-emails`, groupEmailList(group))
                          }
                        >
                          {copiedKey === `${group}-emails` ? "Copied!" : "Copy emails"}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => copyText(`${group}-subject`, subject)}
                        >
                          {copiedKey === `${group}-subject` ? "Copied!" : "Copy subject"}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() =>
                            copyText(`${group}-body`, htmlToPlainText(body))
                          }
                        >
                          {copiedKey === `${group}-body` ? "Copied!" : "Copy message"}
                        </Button>
                      </Box>
                    </Box>
                    {result && (
                      <Alert
                        severity={
                          result.failed.length === 0 ? "success" : "warning"
                        }
                        sx={{ mt: 1.5 }}
                      >
                        ✅ {result.sent} emails sent.
                        {result.failed.length > 0 && (
                          <> ⚠️ Failed: {result.failed.join(", ")}</>
                        )}
                      </Alert>
                    )}
                  </Box>
                );
              })}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setApologyDialogOpen(false)}
              disabled={!!apologySending}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default SeasonRegistrations;
